import API from "../api/axiosInstance";

/**
 * Caddie dropdown vs form visibility (roles vs groups):
 *
 * - CRM role "Caddie" (roles + personroles): who appears in the Select Caddies
 *   dropdown — fetched via GET /api/personRoles/caddies. Not a person _id.
 *
 * - Competition group (participant.group_id → groupName / Group_id in form data):
 *   who sees the "Professional Tour & Caddie Information" panel. Only Athletes
 *   pick a caddie (customConditional groupName === 'Athlete'). Caddie-group
 *   participants do not see that panel by design.
 *
 * Form Builder: Select "Caddies" (key caddies), data source Values (empty OK);
 * options are injected at runtime on the registration form only.
 */

export const REGISTRATION_FORM_ID = "68c7eefbd44999eedb541043";

/** dataGrid.knownCaddies = all picks; person_data.presentCaddies = default [{ _id, name, lname, email }]. */
export const KNOWN_CADDIES_KEY = "knownCaddies";
export const PRESENT_CADDIES_KEY = "presentCaddies";
export const ADDITIONAL_CADDIES_KEY = "additionalCaddies";

/** presentCaddies / additionalCaddies live on person_data root, not inside dataGrid. */
function hoistCaddieArraysToPersonDataRoot(data, primaryGridKey = "dataGrid") {
  if (!data || typeof data !== "object") return data;
  const grid = data[primaryGridKey];
  if (!grid || Array.isArray(grid) || typeof grid !== "object") return data;

  if (
    (!Array.isArray(data[PRESENT_CADDIES_KEY]) || !data[PRESENT_CADDIES_KEY].length) &&
    Array.isArray(grid[PRESENT_CADDIES_KEY])
  ) {
    data[PRESENT_CADDIES_KEY] = grid[PRESENT_CADDIES_KEY];
    delete grid[PRESENT_CADDIES_KEY];
  }
  if (
    data[ADDITIONAL_CADDIES_KEY] == null &&
    Array.isArray(grid[ADDITIONAL_CADDIES_KEY])
  ) {
    data[ADDITIONAL_CADDIES_KEY] = grid[ADDITIONAL_CADDIES_KEY];
    delete grid[ADDITIONAL_CADDIES_KEY];
  }
  return data;
}

function getPresentCaddiesList(data, layout) {
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data[PRESENT_CADDIES_KEY])) return data[PRESENT_CADDIES_KEY];
  const gridKey = layout?.gridKey || "dataGrid";
  const grid = data[gridKey];
  if (grid && typeof grid === "object" && Array.isArray(grid[PRESENT_CADDIES_KEY])) {
    return grid[PRESENT_CADDIES_KEY];
  }
  return [];
}

function getAdditionalCaddiesList(data, layout) {
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data[ADDITIONAL_CADDIES_KEY])) return data[ADDITIONAL_CADDIES_KEY];
  const gridKey = layout?.gridKey || "dataGrid";
  const grid = data[gridKey];
  if (grid && typeof grid === "object" && Array.isArray(grid[ADDITIONAL_CADDIES_KEY])) {
    return grid[ADDITIONAL_CADDIES_KEY];
  }
  return [];
}

/** Known top-level select keys; more keys registered when schema is injected. */
export const CADDIE_SELECT_KEYS = ["caddies", "caddie"];

const CADDIE_FIELD_MAP = {
  caddies: {
    firstName: "givenFirstName1",
    lastName: "caddieLastName",
    email: "familyLastName1",
  },
  caddie: {
    firstName: "givenFirstName1",
    lastName: "caddieLastName",
    email: "familyLastName1",
  },
};

function resolveCaddieFieldMap(selectKey) {
  if (CADDIE_FIELD_MAP[selectKey]) return CADDIE_FIELD_MAP[selectKey];
  if (isTopLevelCaddieSelectKey(selectKey) || /^select.*caddie/i.test(selectKey || "")) {
    return CADDIE_FIELD_MAP.caddies;
  }
  return null;
}

const registeredCaddieSelectKeys = new Set(CADDIE_SELECT_KEYS);
let caddieDatagridLayout = null;

export function resolveApiBaseUrl(override) {
  const trimmed = String(override || process.env.REACT_APP_API_URL || "")
    .trim()
    .replace(/\/$/, "");
  if (trimmed) return trimmed;
  if (process.env.NODE_ENV === "development") return "http://localhost:5000/api";
  return "/api";
}

const safeForEachRow = (rows, cb) => {
  if (!rows || !Array.isArray(rows)) return;
  rows.forEach((row) => {
    if (Array.isArray(row)) row.forEach((cell) => cb(cell));
    else if (row && typeof row === "object") cb(row);
  });
};

const traverseComponents = (components, handler) => {
  if (!components || !Array.isArray(components)) return;
  components.forEach((component) => {
    handler(component);
    if (component.components) traverseComponents(component.components, handler);
    if (component.columns) {
      component.columns.forEach((col) =>
        traverseComponents(col.components || [], handler)
      );
    }
    if (component.rows) {
      safeForEachRow(component.rows, (cell) =>
        cell?.components && traverseComponents(cell.components, handler)
      );
    }
    if (component.datagrid?.components) {
      traverseComponents(component.datagrid.components, handler);
    }
    if (component.editgrid?.components) {
      traverseComponents(component.editgrid.components, handler);
    }
    if (component.tabs) {
      component.tabs.forEach((tab) =>
        traverseComponents(tab.components || [], handler)
      );
    }
  });
};

export function isCaddieSelectComponent(comp) {
  if (!comp || comp.type !== "select") return false;
  const key = String(comp.key || "").trim();
  const label = String(comp.label || "").trim();
  if (CADDIE_SELECT_KEYS.includes(key)) return true;
  if (/^caddies?$/i.test(key)) return true;
  if (/^caddies?$/i.test(label)) return true;
  if (/caddie/i.test(label) && /select/i.test(label)) return true;
  if (/caddie/i.test(label) && /name/i.test(label)) return true;
  if (/^select.*caddie/i.test(key) || /^caddie.*select/i.test(key)) return true;
  return false;
}

/** Change originated inside the caddie datagrid (not the top-level Select Caddies field). */
function isDatagridCaddieChange(changed, layout) {
  if (!changed || !layout?.gridKey) return false;

  const paths = [
    changed?.instance?.path,
    changed?.instance?.dataPath,
    changed?.data?.path,
    changed?.component?.path,
  ].filter(Boolean);
  const gridKey = layout.gridKey;
  if (paths.some((p) => String(p).includes(`${gridKey}[`))) return true;

  let node = changed.instance;
  while (node) {
    if (node.inDataGrid) return true;
    const parentKey = node.parent?.component?.key || node.parent?.key;
    if (parentKey === gridKey) return true;
    const parentType = (node.parent?.component?.type || node.parent?.type || "")
      .toLowerCase();
    if (parentType === "datagrid" || parentType === "editgrid") {
      const pk = node.parent?.component?.key || node.parent?.key;
      if (!pk || pk === gridKey) return true;
    }
    node = node.parent;
  }
  return false;
}

/** Top-level caddie dropdown only (not datagrid row select — even when key is "caddies"). */
export function isTopLevelCaddieSelectKey(key, changed = null, layout = null) {
  if (!key) return false;
  if (key === "select") return false;
  const layoutRef = layout || getCaddieDatagridLayout();
  if (changed && isDatagridCaddieChange(changed, layoutRef)) return false;
  if (registeredCaddieSelectKeys.has(key)) return true;
  return /^caddies?$/i.test(String(key)) || /^select.*caddie/i.test(String(key));
}

/** @deprecated Use isTopLevelCaddieSelectKey or grid change detection. */
export function isCaddieSelectKey(key) {
  return isTopLevelCaddieSelectKey(key);
}

function isDatagridCaddieSelectColumn(col, gridComp) {
  if (!col || col.type !== "select") return false;
  if (isCaddieSelectComponent(col)) return true;
  const gridLabel = `${gridComp?.label || ""} ${gridComp?.key || ""}`.toLowerCase();
  const colLabel = String(col.label || "").toLowerCase();
  if (/caddie/i.test(colLabel) && /name/i.test(colLabel)) return true;
  if (/caddie/i.test(gridLabel) && col.key === "select") return true;
  if (/caddie/i.test(col.label || "")) return true;

  const isCaddieGrid =
    gridComp?.key === "dataGrid" ||
    gridComp?.key === "caddieGrid" ||
    /caddie/i.test(gridLabel);

  // Row caddie picker — even if Form Builder left dataSrc=url (that hang is
  // fixed later by fillCaddieSelectComponent forcing values).
  if (col.key === "select" && isCaddieGrid) return true;

  // Skip guest phone / country URL selects that are not caddie pickers.
  if (col.dataSrc === "url" || col.data?.url) return false;
  return false;
}

/** Radio/checkbox column that marks default/active caddie — never the caddie name dropdown. */
function isDefaultCaddieRadioColumn(col, gridComp) {
  if (!col) return false;
  if (isDatagridCaddieSelectColumn(col, gridComp)) return false;
  const type = (col.type || "").toLowerCase();
  if (type !== "radio" && type !== "checkbox") return false;
  const key = String(col.key || "").toLowerCase();
  const label = String(col.label || "").toLowerCase();
  const gridLabel = `${gridComp?.label || ""} ${gridComp?.key || ""}`.toLowerCase();
  if (!/caddie/i.test(gridLabel) && gridComp?.key !== "dataGrid" && gridComp?.key !== "caddieGrid") {
    return false;
  }
  if (/default|active|primary|selected/i.test(key) || /default|active/i.test(label)) {
    return true;
  }
  if (key === "radio" || label === "radio") return true;
  const vals = col.values || col.data?.values || [];
  if (vals.length === 1) {
    const only = vals[0];
    const v = String(only?.value ?? only?.label ?? "").toLowerCase();
    if (!/^[a-f\d]{24}$/i.test(v)) return true;
  }

  const hasCaddieSelect = (gridComp?.components || []).some((c) =>
    isDatagridCaddieSelectColumn(c, gridComp)
  );
  if (
    hasCaddieSelect &&
    (gridComp?.key === "dataGrid" ||
      gridComp?.key === "caddieGrid" ||
      /caddie/i.test(gridLabel))
  ) {
    const looksLikeCaddieList = vals.some((v) =>
      /^[a-f\d]{24}$/i.test(String(v?.value ?? v?.label ?? ""))
    );
    if (!looksLikeCaddieList) return true;
  }
  return false;
}

/** Form.io RadioComponent calls value.toString() — null crashes attach. */
const DEFAULT_RADIO_OFF = "";

/**
 * Keep the Form Builder radio column as-is; only fix what breaks datagrid runtime.
 * (Labels, values, and layout come from the schema.)
 */
function ensureDefaultCaddieRadioRendersInDatagrid(col) {
  if (!col) return;
  col.input = true;
  col.hidden = false;
  if (col.tableView === false) col.tableView = true;

  if (col.customClass) {
    col.customClass = String(col.customClass)
      .replace(/\bcaddie-default-radio-only\b/g, "")
      .trim();
  }

  const schemaValues = col.values || col.data?.values;
  if (col.dataSrc === "url" || col.data?.url) {
    col.dataSrc = "values";
    col.data = {
      values: Array.isArray(schemaValues) && schemaValues.length ? schemaValues : [],
    };
    delete col.data.url;
  }
  if (col.defaultValue == null) col.defaultValue = DEFAULT_RADIO_OFF;
}

function isCaddieDatagridComponent(comp) {
  if (!comp) return false;
  const type = (comp.type || "").toLowerCase();
  if (type !== "datagrid" && type !== "editgrid") return false;
  const label = `${comp.label || ""} ${comp.key || ""}`.toLowerCase();
  if (comp.key === "dataGrid" || comp.key === "caddieGrid") return true;
  if (/caddie/i.test(label)) return true;
  return (comp.components || []).some((c) => isDatagridCaddieSelectColumn(c, comp));
}

function defaultRadioActiveValue(col) {
  const vals = col?.values || col?.data?.values;
  if (Array.isArray(vals) && vals.length > 0) {
    const v = vals[0].value ?? vals[0].label;
    if (v != null && v !== "") return String(v);
  }
  return "yes";
}

/** Replace null/undefined default-radio cell values before Form.io renders. */
export function sanitizeGridDefaultRadioValues(data, layout = null) {
  if (!data || typeof data !== "object") return data;
  const layoutRef = layout || getCaddieDatagridLayout();
  if (!layoutRef?.defaultRadioKey || !layoutRef?.gridKey) return data;

  const grid = data[layoutRef.gridKey];
  if (!Array.isArray(grid)) return data;

  const key = layoutRef.defaultRadioKey;
  const active = layoutRef.defaultRadioActiveValue ?? "yes";
  data[layoutRef.gridKey] = grid.map((row) => {
    if (!row || typeof row !== "object") return row;
    const val = row[key];
    if (val == null) return { ...row, [key]: DEFAULT_RADIO_OFF };
    if (val === true || val === 1) return { ...row, [key]: active };
    return row;
  });
  return data;
}

function attachDefaultRadioToLayout(layout, comp) {
  if (!layout || !comp) return layout;
  let defaultCol = (comp.components || []).find((c) =>
    isDefaultCaddieRadioColumn(c, comp)
  );
  if (!defaultCol) {
    defaultCol = (comp.components || []).find(
      (c) =>
        (c.type === "radio" || c.type === "checkbox") &&
        !isDatagridCaddieSelectColumn(c, comp)
    );
  }
  if (defaultCol) {
    layout.defaultRadioKey = defaultCol.key;
    layout.defaultRadioActiveValue = defaultRadioActiveValue(defaultCol);
  }
  return layout;
}

function isRowDefaultRadioOn(row, layout) {
  if (!row || !layout?.defaultRadioKey) return false;
  const val = row[layout.defaultRadioKey];
  if (val === true || val === 1) return true;
  if (val == null || val === "" || val === false) return false;
  const active = layout.defaultRadioActiveValue;
  if (active != null && String(val) === String(active)) return true;
  return String(val).trim() !== "";
}

/** Last row with radio on — Form.io datagrid may leave earlier rows "on" in submission data. */
function findLastDefaultRadioRowIndex(grid, layout) {
  if (!Array.isArray(grid) || !layout?.defaultRadioKey) return -1;
  let lastOn = -1;
  grid.forEach((row, i) => {
    if (isRowDefaultRadioOn(row, layout)) lastOn = i;
  });
  return lastOn;
}

/** Detect default-radio edits inside the caddie datagrid (per-row radios do not link in HTML). */
export function isDefaultRadioFormChange(changed, layout = null) {
  const layoutRef = layout || getCaddieDatagridLayout();
  if (!changed || !layoutRef?.defaultRadioKey || !layoutRef?.gridKey) return false;
  const radioKey = layoutRef.defaultRadioKey;
  if (changed?.component?.key === radioKey) return true;
  const type = (changed?.component?.type || "").toLowerCase();
  if (
    (type === "radio" || type === "checkbox") &&
    isDatagridCaddieChange(changed, layoutRef)
  ) {
    return true;
  }
  const paths = [
    changed?.instance?.path,
    changed?.instance?.dataPath,
    changed?.data?.path,
    changed?.component?.path,
  ].filter(Boolean);
  return paths.some((p) => {
    const s = String(p);
    return s.includes(`${layoutRef.gridKey}[`) && s.includes(radioKey);
  });
}

/** Default from live datagrid rows (radio) — source of truth while editing/saving. */
function resolveDefaultCaddieFromLiveGrid(data, layout) {
  if (!data || !layout?.gridKey) return "";
  const grid = data[layout.gridKey];
  if (!Array.isArray(grid)) return "";

  let lastId = "";
  for (const row of grid) {
    if (!isRowDefaultRadioOn(row, layout)) continue;
    const id = extractCaddieIdForStorage(row[layout.selectKey]);
    if (id) lastId = id;
  }
  if (lastId) return lastId;
  if (grid.length === 1) {
    return extractCaddieIdForStorage(grid[0]?.[layout.selectKey]);
  }
  return "";
}

function resolveDefaultCaddieIdFromGrid(data, layout) {
  if (!data || !layout?.gridKey) return "";

  const live = resolveDefaultCaddieFromLiveGrid(data, layout);
  if (live) return live;

  const presentList = getPresentCaddiesList(data, layout);
  if (presentList.length) {
    return extractCaddieIdForStorage(presentList[0]?._id ?? presentList[0]);
  }

  const grid = data[layout.gridKey];
  if (grid && typeof grid === "object" && Array.isArray(grid[KNOWN_CADDIES_KEY])) {
    if (grid[KNOWN_CADDIES_KEY].length === 1) {
      return extractCaddieIdForStorage(
        grid[KNOWN_CADDIES_KEY][0]?._id ?? grid[KNOWN_CADDIES_KEY][0]
      );
    }
  }

  return extractCaddieIdForStorage(data.defaultCaddieId);
}

function syncTopLevelFieldsFromPresent(data, caddiesById) {
  if (!data || typeof data !== "object") return data;
  const layout = getCaddieDatagridLayout();
  const presentList = getPresentCaddiesList(data, layout);
  if (!presentList.length) return data;

  const defaultId = extractCaddieIdForStorage(
    presentList[0]?._id ?? presentList[0]
  );
  if (!isObjectIdStr(defaultId)) return data;

  const map =
    caddiesById instanceof Map
      ? caddiesById
      : caddiesById
        ? buildCaddiesById(caddiesById)
        : new Map();
  const person = map.get(defaultId) || presentList[0];

  CADDIE_SELECT_KEYS.forEach((key) => {
    if (!isTopLevelCaddieSelectKey(key, null, layout)) return;
    applyPersonToFlatFields(data, person, key);
  });
  return data;
}

function syncPresentCaddieFromGrid(data, layout, caddiesById, options = {}) {
  if (!data || !layout) return data;
  const { syncFlatFields = true } = options;

  let defaultId = resolveDefaultCaddieFromLiveGrid(data, layout);
  const grid = data[layout.gridKey];

  if (!defaultId && Array.isArray(grid)) {
    const presentList = getPresentCaddiesList(data, layout);
    const presentId = extractCaddieIdForStorage(
      presentList[0]?._id ?? presentList[0]
    );
    if (isObjectIdStr(presentId)) {
      defaultId = presentId;
      const idx = grid.findIndex(
        (r) => extractCaddieIdForStorage(r[layout.selectKey]) === presentId
      );
      if (idx >= 0 && layout.defaultRadioKey) {
        applyExclusiveDefaultRadioAtIndex(data, layout, idx);
      }
    }
  }

  if (!defaultId || !isObjectIdStr(defaultId)) {
    data[PRESENT_CADDIES_KEY] = [];
    return data;
  }

  const emailKey = layout.emailKey || "familyLastName4";
  const rowEmailById = new Map();
  if (Array.isArray(grid)) {
    grid.forEach((row) => {
      const id = extractCaddieIdForStorage(row[layout.selectKey]);
      if (!id) return;
      const em = emailKey ? String(row[emailKey] || "").trim() : "";
      if (em) rowEmailById.set(id, em);
    });
  }

  data[PRESENT_CADDIES_KEY] = [
    buildCaddieStorageRecord(defaultId, caddiesById, rowEmailById),
  ];
  if (syncFlatFields) {
    syncTopLevelFieldsFromPresent(data, caddiesById);
  }
  return data;
}

/** Grid radio + presentCaddies + top-level caddie fields share one default id. */
function reconcileDefaultCaddieDisplay(data, layout, caddiesById) {
  if (!data || !layout?.gridKey) return data;
  const grid = Array.isArray(data[layout.gridKey]) ? data[layout.gridKey] : [];
  if (!grid.length) return data;

  const map =
    caddiesById instanceof Map
      ? caddiesById
      : caddiesById
        ? buildCaddiesById(caddiesById)
        : new Map();

  if (layout.defaultRadioKey && !grid.some((r) => isRowDefaultRadioOn(r, layout))) {
    ensureDefaultCaddieIfNone(data, layout);
  }
  sanitizeGridDefaultRadioValues(data, layout);
  syncPresentCaddieFromGrid(data, layout, map);

  return data;
}

function applyDefaultCaddieRadioExclusive(data, layout, changed) {
  if (!data || !layout?.defaultRadioKey || !layout?.gridKey) return data;
  const grid = Array.isArray(data[layout.gridKey])
    ? data[layout.gridKey].map((r) => ({ ...r }))
    : [];
  if (!grid.length) return data;

  const activeVal = layout.defaultRadioActiveValue;
  const radioKey = layout.defaultRadioKey;
  const changedRow = resolveDatagridRowIndex(changed, layout, data);

  let selectedIdx = -1;
  if (
    changedRow >= 0 &&
    changedRow < grid.length &&
    isDefaultRadioFormChange(changed, layout)
  ) {
    selectedIdx = changedRow;
  } else if (changedRow >= 0 && isRowDefaultRadioOn(grid[changedRow], layout)) {
    selectedIdx = changedRow;
  } else {
    selectedIdx = findLastDefaultRadioRowIndex(grid, layout);
  }

  data[layout.gridKey] = grid.map((row, i) => ({
    ...row,
    [radioKey]:
      i === selectedIdx && selectedIdx >= 0 ? activeVal : DEFAULT_RADIO_OFF,
  }));
  return data;
}

function readDefaultRadioOnFromInstance(inst, layout) {
  if (!inst || !layout?.defaultRadioKey) return false;
  const key = inst.component?.key || inst.key;
  if (key !== layout.defaultRadioKey) return false;

  let val;
  try {
    val = inst.dataValue;
  } catch {
    val = undefined;
  }
  if (val === undefined && typeof inst.getValue === "function") {
    try {
      val = inst.getValue();
    } catch {
      val = undefined;
    }
  }
  if (isRowDefaultRadioOn({ [layout.defaultRadioKey]: val }, layout)) return true;
  if (inst.checked === true) return true;

  const root = inst.element || inst.refs?.input;
  if (root && typeof root.querySelector === "function") {
    if (root.querySelector("input:checked")) return true;
  }
  if (root?.checked === true) return true;
  return false;
}

/** Which datagrid row has the default radio checked in the live Form.io UI. */
function getSelectedDefaultRadioRowFromFormio(formio, layout) {
  if (!formio || !layout?.gridKey || !layout?.defaultRadioKey) return -1;
  const gridComp = formio.getComponent?.(layout.gridKey);
  if (!gridComp) return -1;

  let selectedIdx = -1;
  const rowList = Array.isArray(gridComp.rows)
    ? gridComp.rows
    : gridComp.rows && typeof gridComp.rows === "object"
      ? Object.keys(gridComp.rows)
          .filter((k) => /^\d+$/.test(k))
          .sort((a, b) => Number(a) - Number(b))
          .map((k) => gridComp.rows[k])
      : [];

  rowList.forEach((rowComps, i) => {
    if (!rowComps || typeof rowComps !== "object") return;
    const comps = Array.isArray(rowComps) ? rowComps : Object.values(rowComps);
    comps.forEach((inst) => {
      if (readDefaultRadioOnFromInstance(inst, layout)) selectedIdx = i;
    });
  });

  if (selectedIdx < 0 && gridComp.element) {
    const rowEls = gridComp.element.querySelectorAll(
      ".datagrid-row, .editgrid-row, tbody tr"
    );
    rowEls.forEach((rowEl, i) => {
      const checked = rowEl.querySelector(
        `input[type="radio"]:checked, .formio-component-${layout.defaultRadioKey} input:checked`
      );
      if (checked) selectedIdx = i;
    });
  }

  return selectedIdx;
}

function applyExclusiveDefaultRadioAtIndex(data, layout, selectedIdx) {
  if (!data || !layout?.defaultRadioKey || !layout?.gridKey || selectedIdx < 0) {
    return data;
  }
  const grid = Array.isArray(data[layout.gridKey])
    ? data[layout.gridKey].map((r) => ({ ...r }))
    : [];
  if (selectedIdx >= grid.length) return data;

  const activeVal = layout.defaultRadioActiveValue;
  const radioKey = layout.defaultRadioKey;
  data[layout.gridKey] = grid.map((row, i) => ({
    ...row,
    [radioKey]: i === selectedIdx ? activeVal : DEFAULT_RADIO_OFF,
  }));
  return data;
}

/**
 * Merge caddie datagrid row values from live Form.io row instances (radio/select/email).
 * submission.data is often stale for per-row radios after switching rows.
 */
export function mergeCaddieGridFromFormioInstance(formio, data) {
  if (!formio || !data || typeof data !== "object") return data;
  const layout = getCaddieDatagridLayout();
  if (!layout?.gridKey) return data;

  const gridComp = formio.getComponent?.(layout.gridKey);
  if (!gridComp) return data;

  const next = JSON.parse(JSON.stringify(data));
  const existing = Array.isArray(next[layout.gridKey]) ? next[layout.gridKey] : [];
  const rowInstances = Array.isArray(gridComp.rows) ? gridComp.rows : [];
  const rowCount = Math.max(existing.length, rowInstances.length);
  if (!rowCount) return next;

  const rows = [];
  for (let i = 0; i < rowCount; i++) {
    const row = { ...(existing[i] || {}) };
    const rowComps = rowInstances[i];
    if (rowComps && typeof rowComps === "object") {
      Object.values(rowComps).forEach((inst) => {
        if (!inst) return;
        const key = inst.component?.key || inst.key;
        if (!key) return;
        let val;
        try {
          val = inst.dataValue;
        } catch {
          val = undefined;
        }
        if (val === undefined && typeof inst.getValue === "function") {
          try {
            val = inst.getValue();
          } catch {
            val = undefined;
          }
        }
        if (val !== undefined) row[key] = val;
      });
    }
    rows.push(row);
  }

  const selectedIdx = getSelectedDefaultRadioRowFromFormio(formio, layout);
  if (selectedIdx >= 0 && layout.defaultRadioKey) {
    const activeVal = layout.defaultRadioActiveValue;
    const radioKey = layout.defaultRadioKey;
    next[layout.gridKey] = rows.map((row, i) => ({
      ...row,
      [radioKey]: i === selectedIdx ? activeVal : DEFAULT_RADIO_OFF,
    }));
  } else {
    next[layout.gridKey] = rows;
  }
  return next;
}

/** Default caddie id for storage — live UI, then presentCaddies, then stored grid. */
function resolveDefaultCaddieIdForStorage(data, layout) {
  if (!data || !layout) return "";

  const presentList = getPresentCaddiesList(data, layout);
  const presentId = presentList.length
    ? extractCaddieIdForStorage(presentList[0]?._id ?? presentList[0])
    : "";

  const grid = data[layout.gridKey];
  const liveId = resolveDefaultCaddieFromLiveGrid(data, layout);
  const multiOn =
    Array.isArray(grid) &&
    grid.filter((r) => isRowDefaultRadioOn(r, layout)).length > 1;

  if (multiOn && presentId) return presentId;
  if (liveId) return liveId;
  if (presentId) return presentId;

  if (!Array.isArray(grid) && grid?.[KNOWN_CADDIES_KEY] && presentId) {
    return presentId;
  }

  return resolveDefaultCaddieIdFromGrid(data, layout);
}

/** Before save / after load: valid radio values + at most one default selected. */
export function finalizeCaddieGridForSubmit(data, caddiesById = null, formio = null) {
  if (!data || typeof data !== "object") return data;
  const layout = getCaddieDatagridLayout();
  if (!layout?.gridKey) return data;
  let next = JSON.parse(JSON.stringify(data));
  if (formio) {
    next = mergeCaddieGridFromFormioInstance(formio, next);
  }
  sanitizeGridDefaultRadioValues(next, layout);
  if (layout.defaultRadioKey) {
    const selectedIdx = formio ? getSelectedDefaultRadioRowFromFormio(formio, layout) : -1;
    if (selectedIdx >= 0) {
      applyExclusiveDefaultRadioAtIndex(next, layout, selectedIdx);
    } else {
      const presentId = extractCaddieIdForStorage(
        getPresentCaddiesList(next, layout)[0]?._id ?? getPresentCaddiesList(next, layout)[0]
      );
      const grid = Array.isArray(next[layout.gridKey]) ? next[layout.gridKey] : [];
      const presentRowIdx =
        presentId && grid.length
          ? grid.findIndex(
              (r) => extractCaddieIdForStorage(r[layout.selectKey]) === presentId
            )
          : -1;
      if (presentRowIdx >= 0) {
        applyExclusiveDefaultRadioAtIndex(next, layout, presentRowIdx);
      } else {
        applyDefaultCaddieRadioExclusive(next, layout, {});
      }
    }
  }
  ensureDefaultCaddieIfNone(next, layout);
  const out = syncPresentCaddieFromGrid(next, layout, caddiesById || new Map());
  return out;
}

/**
 * Build save payload from live Form.io; keep last good snapshot when later panel
 * submits clear radios (multi-panel forms fire onSubmit per panel).
 */
export function resolveCaddieSubmitPayload(
  data,
  caddiesById = null,
  formio = null,
  lastGoodRef = null
) {
  const prepared = finalizeCaddieGridForSubmit(data, caddiesById, formio);
  const layout = getCaddieDatagridLayout();
  const presentId = String(prepared?.presentCaddies?.[0]?._id || "");
  const refPresent = String(lastGoodRef?.current?.presentCaddies?.[0]?._id || "");
  const selectedIdx =
    formio && layout ? getSelectedDefaultRadioRowFromFormio(formio, layout) : -1;
  const liveFromGrid = layout ? resolveDefaultCaddieFromLiveGrid(prepared, layout) : "";

  const useFresh =
    presentId &&
    (selectedIdx >= 0 ||
      presentId !== refPresent ||
      (liveFromGrid && liveFromGrid === presentId));

  if (useFresh) {
    if (lastGoodRef) lastGoodRef.current = prepared;
    return prepared;
  }
  if (lastGoodRef?.current) return lastGoodRef.current;
  if (presentId && lastGoodRef) lastGoodRef.current = prepared;
  return prepared;
}

/**
 * Form.io display shape: knownCaddies → rows, default radio, CRM prefill.
 * Call during submit (before schema re-render) and after load/save.
 */
export function prepareCaddieGridDisplayData(
  data,
  caddiesById = null,
  formio = null,
  options = {}
) {
  if (!data || typeof data !== "object") return data;
  const { syncFormio = true, gridOnlySync = true, forSubmit = false } = options;
  const layout = resolveLayoutForCaddieRestore(data) || getCaddieDatagridLayout();
  if (!layout?.gridKey) return data;

  const cloned = JSON.parse(JSON.stringify(data));
  const grid = cloned[layout.gridKey];
  const alreadyLiveRows =
    Array.isArray(grid) && grid.some((row) => isCaddieGridRowForStorage(row));

  let next = alreadyLiveRows
    ? cloned
    : normalizeDataGridForFormioRender(cloned);
  next = finalizeCaddieGridForSubmit(next, caddiesById, formio);
  if (
    !forSubmit &&
    ((caddiesById instanceof Map && caddiesById.size) ||
      (Array.isArray(caddiesById) && caddiesById.length))
  ) {
    next = prefillCaddieDatagridRows(next, caddiesById);
  }
  if (formio && syncFormio && !forSubmit) {
    pushFormioSubmissionData(formio, next, { gridOnly: gridOnlySync });
  }
  return next;
}

function ensureDefaultCaddieIfNone(data, layout, preferIndex = -1) {
  if (!layout?.defaultRadioKey || !layout?.gridKey) return data;
  const grid = Array.isArray(data[layout.gridKey]) ? data[layout.gridKey] : [];
  if (!grid.length) return data;
  if (grid.some((r) => isRowDefaultRadioOn(r, layout))) return data;

  let idx = preferIndex;
  if (idx < 0) {
    const presentList = getPresentCaddiesList(data, layout);
    const presentId = extractCaddieIdForStorage(
      presentList[0]?._id ?? presentList[0]
    );
    if (isObjectIdStr(presentId)) {
      idx = grid.findIndex(
        (r) => extractCaddieIdForStorage(r[layout.selectKey]) === presentId
      );
    }
  }
  if (idx < 0 || !normalizeSelectedIds(grid[idx]?.[layout.selectKey])?.length) {
    idx = grid.findIndex((r) => normalizeSelectedIds(r[layout.selectKey])?.length);
  }
  if (idx < 0) return data;

  const activeVal = layout.defaultRadioActiveValue;
  data[layout.gridKey] = grid.map((row, i) => ({
    ...row,
    [layout.defaultRadioKey]: i === idx ? activeVal : DEFAULT_RADIO_OFF,
  }));
  return data;
}

function buildCaddieStorageRecord(idStr, caddiesById, rowEmailById) {
  const map =
    caddiesById instanceof Map ? caddiesById : buildCaddiesById(caddiesById);
  const person = map.get(idStr);
  return {
    _id: idStr,
    name: person?.name || "",
    lname: person?.lname || "",
    // Prefer email already on the row / knownCaddies over empty CRM Person.email
    email:
      rowEmailById?.get?.(idStr) ||
      resolveCaddieEmail(person) ||
      "",
  };
}

/** One row per caddie person id (avoids duplicate select rows after restore/sync). */
function dedupeGridRowsByCaddieId(rows, layout) {
  if (!Array.isArray(rows) || !layout?.selectKey) return rows;
  const selectKey = layout.selectKey;
  const seen = new Set();
  const out = [];
  rows.forEach((row) => {
    const id = extractCaddieIdForStorage(row?.[selectKey]);
    if (id && seen.has(id)) return;
    if (id) seen.add(id);
    out.push(row);
  });
  return out;
}

function buildKnownCaddieRowForRender(kc, layout, defaultIdStr) {
  if (!kc) return null;
  const rawId =
    typeof kc === "object" && kc !== null && !Array.isArray(kc)
      ? kc._id ?? kc.id ?? kc.value ?? kc
      : kc;
  const idStr = extractCaddieIdForStorage(rawId);
  if (!isObjectIdStr(idStr)) return null;

  const selectKey = layout?.selectKey || "caddies";
  const emailKey = layout?.emailKey || "familyLastName4";
  const defaultNorm = extractCaddieIdForStorage(defaultIdStr);
  const row = {
    [selectKey]: idStr,
    ...(emailKey
      ? {
          [emailKey]: String(
            resolveCaddieEmail(kc) || kc[emailKey] || ""
          ).trim(),
        }
      : {}),
  };
  if (layout?.defaultRadioKey) {
    const isDefault = Boolean(defaultNorm && idStr === defaultNorm);
    row[layout.defaultRadioKey] = isDefault
      ? layout.defaultRadioActiveValue
      : DEFAULT_RADIO_OFF;
  }
  return row;
}

/** Layout from schema, or infer from stored dataGrid.knownCaddies before schema is ready. */
function resolveLayoutForCaddieRestore(data) {
  const fromSchema = getCaddieDatagridLayout();
  if (fromSchema?.selectKey && fromSchema?.gridKey) return fromSchema;

  const inferred = {
    gridKey: "dataGrid",
    selectKey: "caddies",
    emailKey: "familyLastName4",
    defaultRadioKey: "radio",
    defaultRadioActiveValue: "yes",
    mode: "caddieSelectName",
  };

  const gridKeys = ["dataGrid", "caddieGrid", "dataGrid1"];
  for (const gridKey of gridKeys) {
    const grid = data?.[gridKey];
    if (
      grid &&
      typeof grid === "object" &&
      !Array.isArray(grid) &&
      Array.isArray(grid[KNOWN_CADDIES_KEY]) &&
      grid[KNOWN_CADDIES_KEY].length
    ) {
      return { ...inferred, gridKey };
    }
  }

  if (Array.isArray(data?.[KNOWN_CADDIES_KEY]) && data[KNOWN_CADDIES_KEY].length) {
    return {
      ...inferred,
      gridKey: fromSchema?.gridKey || "dataGrid",
    };
  }

  const present = getPresentCaddiesList(data, fromSchema);
  if (present.length) {
    return {
      ...inferred,
      gridKey: fromSchema?.gridKey || "dataGrid",
    };
  }

  return fromSchema;
}

function buildLayoutFromDatagrid(comp) {
  const cols = comp.components || [];
  if (!cols.length) return null;

  const email = cols.find(
    (c) =>
      /email/i.test(c.label || "") ||
      [
        "familyLastName1",
        "familyLastName4",
        "caddieEmail",
        "email",
      ].includes(c.key)
  );
  const phone = cols.find(
    (c) =>
      /phone/i.test(c.label || "") ||
      ["phoneNumber", "phone", "fullphonenumber"].includes(c.key)
  );
  const fullName = cols.find(
    (c) => c.key === "fullName" || /full\s*name/i.test(c.label || "")
  );
  const first = cols.find(
    (c) =>
      (/first/i.test(c.label || "") && !/full/i.test(c.label || "")) ||
      ["givenFirstName1", "caddieFirstName", "firstName"].includes(c.key)
  );
  const last = cols.find(
    (c) =>
      /last/i.test(c.label || "") ||
      ["caddieLastName", "caddieLastName1", "lastName"].includes(c.key)
  );
  const selectCol = cols.find((c) => isDatagridCaddieSelectColumn(c, comp));

  /** Caddie Name column is the dropdown; Caddie Email is a separate text field. */
  if (selectCol && email) {
    return attachDefaultRadioToLayout(
      {
        gridKey: comp.key,
        mode: "caddieSelectName",
        emailKey: email.key,
        phoneKey: phone?.key,
        selectKey: selectCol.key,
      },
      comp
    );
  }

  let layout = null;
  if (fullName && email) {
    layout = {
      gridKey: comp.key,
      mode: "fullName",
      fullNameKey: fullName.key,
      emailKey: email.key,
      phoneKey: phone?.key,
    };
  } else if (first && email) {
    layout = {
      gridKey: comp.key,
      mode: "firstLast",
      firstNameKey: first.key,
      lastNameKey: last?.key || null,
      emailKey: email.key,
      phoneKey: phone?.key,
    };
  }

  if (layout && selectCol) layout.selectKey = selectCol.key;
  return layout ? attachDefaultRadioToLayout(layout, comp) : null;
}

export function discoverCaddieDatagridLayout(components) {
  let fallback = null;
  let withCaddieSelect = null;

  traverseComponents(components, (comp) => {
    const type = (comp.type || "").toLowerCase();
    if (type !== "datagrid" && type !== "editgrid") return;
    const layout = buildLayoutFromDatagrid(comp);
    if (!layout?.selectKey) return;

    if (isCaddieDatagridComponent(comp)) withCaddieSelect = layout;
    else if (!fallback) fallback = layout;
  });

  return withCaddieSelect || fallback;
}

export function ensureCaddieDatagridLayoutFromSchema(components) {
  if (!Array.isArray(components)) return getCaddieDatagridLayout();
  const layout = discoverCaddieDatagridLayout(components);
  if (layout) caddieDatagridLayout = layout;
  return caddieDatagridLayout;
}

export function getCaddieDatagridLayout() {
  return caddieDatagridLayout;
}

const CADDIES_CACHE_TTL_MS = 5 * 60 * 1000;
let caddiesListCache = { base: "", data: null, fetchedAt: 0 };

export async function fetchCaddieOptions(apiUrl) {
  const base = resolveApiBaseUrl(apiUrl);
  const now = Date.now();
  if (
    caddiesListCache.data &&
    caddiesListCache.base === base &&
    now - caddiesListCache.fetchedAt < CADDIES_CACHE_TTL_MS
  ) {
    return caddiesListCache.data;
  }

  try {
    const res = await API.get("/personRoles/caddies", {
      baseURL: base,
      timeout: 15000,
    });
    const data = Array.isArray(res.data) ? res.data : [];
    caddiesListCache = { base, data, fetchedAt: now };
    return data;
  } catch (err) {
    console.error("fetchCaddieOptions failed:", err?.message || err);
    return caddiesListCache.base === base ? caddiesListCache.data || [] : [];
  }
}

export function formatCaddieDisplayName(person) {
  if (!person) return "";
  return `${person.name || ""} ${person.lname || ""}`.trim();
}

export function buildCaddiesById(caddies) {
  const map = new Map();
  (caddies || []).forEach((p) => {
    if (p?._id != null) map.set(String(p._id), p);
  });
  return map;
}

function normalizeSelectedIds(raw) {
  if (raw == null || raw === "") return [];
  const list = Array.isArray(raw) ? raw : [raw];
  return list
    .map((item) => {
      if (item == null || item === "") return "";
      if (typeof item === "object") {
        return String(item.value ?? item._id ?? "");
      }
      return String(item);
    })
    .filter(Boolean);
}

/** Collect known/present caddie records from person_data (root or nested on grids). */
function collectKnownCaddieRecords(data) {
  const out = [];
  const indexById = new Map();

  const addOrEnrich = (entry) => {
    if (entry == null) return;
    let record;
    if (typeof entry === "object" && !Array.isArray(entry)) {
      const idStr = extractCaddieIdForStorage(entry._id ?? entry.id ?? entry.value);
      if (!isObjectIdStr(idStr)) return;
      record = { ...entry, _id: idStr };
    } else {
      const idStr = extractCaddieIdForStorage(entry);
      if (!isObjectIdStr(idStr)) return;
      record = { _id: idStr };
    }

    const email = resolveCaddieEmail(record);
    const existingIdx = indexById.get(record._id);
    if (existingIdx != null) {
      const existing = out[existingIdx];
      if (email && !resolveCaddieEmail(existing)) existing.email = email;
      if (record.name && !existing.name) existing.name = record.name;
      if (record.lname && !existing.lname) existing.lname = record.lname;
      return;
    }
    indexById.set(record._id, out.length);
    out.push(record);
  };

  const addList = (list) => {
    if (!Array.isArray(list)) return;
    list.forEach(addOrEnrich);
  };

  if (!data || typeof data !== "object") return out;
  addList(data[KNOWN_CADDIES_KEY]);
  addList(data[PRESENT_CADDIES_KEY]);

  const layout = getCaddieDatagridLayout();
  const selectKey = layout?.selectKey || "caddies";
  const emailKey = layout?.emailKey || "familyLastName4";

  ["dataGrid", "caddieGrid", "dataGrid1"].forEach((gridKey) => {
    const grid = data[gridKey];
    if (!grid) return;
    if (Array.isArray(grid)) {
      // After knownCaddies → Form.io rows, harvest id + email from each row.
      grid.forEach((row) => {
        if (!row || typeof row !== "object") return;
        const idStr = extractCaddieIdForStorage(row[selectKey] ?? row._id);
        if (!isObjectIdStr(idStr)) return;
        addOrEnrich({
          _id: idStr,
          name: row.name || "",
          lname: row.lname || "",
          email: String(row[emailKey] || row.email || "").trim(),
        });
      });
      return;
    }
    if (typeof grid === "object") {
      addList(grid[KNOWN_CADDIES_KEY]);
      addList(grid[PRESENT_CADDIES_KEY]);
    }
  });

  rememberKnownCaddieEmails(out);
  return out;
}

/**
 * Merge saved knownCaddies / presentCaddies into the CRM list.
 * - Always keep known ids in the dropdown (even if missing from CRM role list).
 * - Prefer person_data emails/names when CRM fields are empty (or known has a value).
 */
export function mergeKnownCaddiesIntoOptionsList(caddies, submissionData) {
  const out = Array.isArray(caddies)
    ? caddies.map((p) => (p && typeof p === "object" ? { ...p } : p))
    : [];
  const indexById = new Map();
  out.forEach((p, i) => {
    if (p?._id != null) indexById.set(String(p._id), i);
  });

  collectKnownCaddieRecords(submissionData).forEach((kc) => {
    const id = String(kc._id);
    const knownEmail = resolveCaddieEmail(kc);
    const knownName = String(kc.name || "").trim();
    const knownLname = String(kc.lname || "").trim();
    const knownLabel = String(
      kc.label || formatCaddieDisplayName(kc) || ""
    ).trim();

    const existingIdx = indexById.get(id);
    if (existingIdx != null) {
      const existing = out[existingIdx] || {};
      const enriched = {
        ...existing,
        _id: id,
        // Prefer stored participant knownCaddies when present — that is the
        // source of truth for name/email already saved on the person.
        name: knownName || existing.name || "",
        lname: knownLname || existing.lname || "",
        email: knownEmail || resolveCaddieEmail(existing) || "",
        email2: kc.email2 || existing.email2 || "",
        label:
          knownLabel ||
          existing.label ||
          formatCaddieDisplayName({
            name: knownName || existing.name,
            lname: knownLname || existing.lname,
          }) ||
          id,
      };
      out[existingIdx] = enriched;
      return;
    }

    const person = {
      _id: id,
      name: knownName,
      lname: knownLname,
      email: knownEmail,
      email2: kc.email2 || "",
      label: knownLabel || id,
    };
    indexById.set(id, out.length);
    out.push(person);
  });

  rememberKnownCaddieEmails(out);
  return out;
}

/** IDs already stored on the form (top-level select + datagrid rows + knownCaddies). */
function collectSelectedCaddieIdsFromData(data, layout) {
  const ids = new Set();
  if (!data || typeof data !== "object") return ids;

  const addFromKey = (key) => {
    if (data[key] == null || data[key] === "") return;
    normalizeSelectedIds(data[key]).forEach((id) => ids.add(id));
  };

  CADDIE_SELECT_KEYS.forEach(addFromKey);
  registeredCaddieSelectKeys.forEach(addFromKey);

  if (layout?.gridKey && layout.selectKey && Array.isArray(data[layout.gridKey])) {
    data[layout.gridKey].forEach((row) => {
      normalizeSelectedIds(row?.[layout.selectKey]).forEach((id) => ids.add(id));
    });
  }

  collectKnownCaddieRecords(data).forEach((kc) => {
    const idStr = String(kc._id);
    if (isObjectIdStr(idStr)) ids.add(idStr);
  });

  return ids;
}

/** Label for a saved caddie id that is not in the CRM list (uses grid / flat name fields). */
function labelForOrphanCaddieId(id, data, layout) {
  const idStr = String(id);
  if (!data) return "";

  const known = collectKnownCaddieRecords(data).find(
    (kc) => String(kc._id) === idStr
  );
  if (known) {
    const fromKnown = formatCaddieDisplayName(known);
    if (fromKnown) return fromKnown;
  }

  if (layout?.gridKey && layout.selectKey) {
    for (const row of data[layout.gridKey] || []) {
      if (normalizeSelectedIds(row?.[layout.selectKey])[0] !== idStr) continue;
      if (layout.mode === "caddieSelectName" && row[layout.emailKey]) {
        return String(row[layout.emailKey]).trim();
      }
      if (layout.mode === "fullName" && row[layout.fullNameKey]) {
        return String(row[layout.fullNameKey]).trim();
      }
      const first = layout.firstNameKey ? row[layout.firstNameKey] : "";
      const last = layout.lastNameKey ? row[layout.lastNameKey] : "";
      const combined = `${first || ""} ${last || ""}`.trim();
      if (combined) return combined;
      if (layout.emailKey && row[layout.emailKey]) {
        return String(row[layout.emailKey]).trim();
      }
    }
  }

  for (const key of [...CADDIE_SELECT_KEYS, ...registeredCaddieSelectKeys]) {
    if (!normalizeSelectedIds(data[key]).includes(idStr)) continue;
    const fieldMap = resolveCaddieFieldMap(key);
    if (!fieldMap) continue;
    const combined = `${data[fieldMap.firstName] || ""} ${data[fieldMap.lastName] || ""}`.trim();
    if (combined) return combined;
    if (data[fieldMap.email]) return String(data[fieldMap.email]).trim();
  }

  return "";
}

/**
 * Dropdown options: CRM caddies (GET /personRoles/caddies) plus any already-saved ids
 * so Form.io/Choices.js shows names instead of raw ObjectIds.
 */
export function buildCaddieSelectOptions(caddies, submissionData = null) {
  const layout = getCaddieDatagridLayout();
  const merged = mergeKnownCaddiesIntoOptionsList(caddies, submissionData);
  const byId = buildCaddiesById(merged);
  const values = [];
  const seen = new Set();

  merged.forEach((p) => {
    const value = String(p._id);
    if (seen.has(value)) return;
    seen.add(value);
    values.push({
      label: p.label || formatCaddieDisplayName(p) || value,
      value,
      email: resolveCaddieEmail(p),
    });
  });

  collectSelectedCaddieIdsFromData(submissionData, layout).forEach((id) => {
    if (seen.has(id)) return;
    seen.add(id);
    const person = byId.get(id);
    const label =
      (person && (person.label || formatCaddieDisplayName(person))) ||
      labelForOrphanCaddieId(id, submissionData, layout) ||
      id;
    values.push({
      label,
      value: id,
      email: resolveCaddieEmail(person),
    });
  });

  return values;
}

function fillCaddieSelectComponent(comp, caddies, submissionData) {
  const values = buildCaddieSelectOptions(caddies, submissionData);
  // Always force Values source — Form Builder URL/resource sources leave the
  // dropdown empty or hanging when clicked.
  comp.dataSrc = "values";
  comp.data = { ...(comp.data || {}), values };
  if (comp.data.url) delete comp.data.url;
  delete comp.selectValues;
  delete comp.searchField;
  delete comp.filter;
  comp.valueProperty = "value";
  comp.template = "<span>{{ item.label }}</span>";
  comp.widget = "choicesjs";
  comp.searchEnabled = true;
  // Keep list under the field (Form.io datagrid default uses position:fixed).
  comp.dropdown = "bottom";
  comp.lazyLoad = false;
  // Ensure Choices refreshes when options are injected after first mount.
  if (comp.selectThreshold == null) comp.selectThreshold = 0;
}

/**
 * Native Form.io fill: when Caddie Name (select) changes, Caddie Email recalculates
 * from the CRM email map injected at load (works for admin + user without React timing).
 */
function wireCaddieEmailCalculateValue(gridComp, caddies, layout) {
  if (!gridComp || !layout?.selectKey || !layout?.emailKey) return;
  const emailCol = (gridComp.components || []).find(
    (c) => c.key === layout.emailKey
  );
  if (!emailCol) return;

  const emailById = {};
  const emailByLabel = {};
  (caddies || []).forEach((p) => {
    const email = lookupCaddieEmail(p);
    if (!email) return;
    const id = String(p._id);
    emailById[id] = email;
    const label = String(p.label || formatCaddieDisplayName(p) || "").trim();
    if (label) emailByLabel[label] = email;
  });
  knownCaddieEmailById.forEach((email, id) => {
    if (email && !emailById[id]) emailById[id] = email;
  });

  const selectKey = layout.selectKey;
  // Keep calculated, but allow React/setValue to win when CRM map is stale.
  // CRITICAL: never assign '' when a caddie is selected but missing from the map —
  // that wipes a successful setValue/forceFill (knownCaddies email).
  emailCol.allowCalculateOverride = true;
  emailCol.calculateValue = `
    var __sel = row && row[${JSON.stringify(selectKey)}];
    var __fromObj = '';
    if (__sel && typeof __sel === 'object') {
      __fromObj = String(
        __sel.email ||
        __sel.Email ||
        (__sel.customProperties && __sel.customProperties.email) ||
        ''
      ).trim();
      __sel = __sel.value || __sel._id || __sel.id || '';
    }
    __sel = __sel == null ? '' : String(__sel).trim();
    var __byId = ${JSON.stringify(emailById)};
    var __byLabel = ${JSON.stringify(emailByLabel)};
    var __found = __fromObj || (__sel && (__byId[__sel] || __byLabel[__sel])) || '';
    if (__found) {
      value = __found;
    } else if (!__sel) {
      value = '';
    }
  `;
}

/** Write email into the visible <input> for a datagrid row (Form.io setValue can lag). */
function fillCaddieEmailDomInput(formio, layout, rowIndex, email) {
  if (!formio || !layout?.emailKey || rowIndex < 0) return;
  const root = formio.element || formio.ce || null;
  if (!root || typeof root.querySelectorAll !== "function") return;

  const gridRoot =
    root.querySelector(`.formio-component-${layout.gridKey}`) || root;
  const rows = gridRoot.querySelectorAll(
    "table.datagrid-table tbody tr, .datagrid-table tbody tr, tbody tr"
  );
  const tr = rows[rowIndex];
  if (!tr) return;

  const input =
    tr.querySelector(`.formio-component-${layout.emailKey} input`) ||
    tr.querySelector(`input[name*="${layout.emailKey}"]`) ||
    tr.querySelector(`input[id*="${layout.emailKey}"]`);
  if (!input) return;

  const nativeSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    "value"
  )?.set;
  if (nativeSetter) nativeSetter.call(input, email);
  else input.value = email;
  input.dispatchEvent(new Event("input", { bubbles: true }));
  input.dispatchEvent(new Event("change", { bubbles: true }));
}

/** Set the live email textfield for one datagrid row (immediate UI update). */
function forceFillCaddieRowEmail(formio, data, layout, changed) {
  if (!formio || !layout?.gridKey || !layout?.emailKey || !data) return;
  const rowIndex = resolveDatagridRowIndex(changed, layout, data);
  if (rowIndex < 0) return;
  const row = Array.isArray(data[layout.gridKey])
    ? data[layout.gridKey][rowIndex]
    : null;
  if (!row) return;
  let email = String(row[layout.emailKey] || "").trim();
  if (!email && layout.selectKey) {
    const id = normalizeSelectedIds(row[layout.selectKey])[0];
    email = lookupCaddieEmail(id, data);
    if (!email) {
      // Fallback: match knownCaddies by display name on the select option label
      const label = String(
        changed?.instance?.selectedOptions?.[0]?.label ||
          changed?.instance?.dataValue?.label ||
          ""
      ).trim();
      if (label) {
        const needle = label.toLowerCase();
        const known = collectKnownCaddieRecords(data).find((kc) => {
          const n = formatCaddieDisplayName(kc).toLowerCase();
          return n && n === needle;
        });
        email = resolveCaddieEmail(known);
        if (email && id) knownCaddieEmailById.set(id, email);
      }
    }
    if (email) row[layout.emailKey] = email;
  }
  if (!email) {
    if (normalizeSelectedIds(row[layout.selectKey] || []).length) return;
    // Cleared select → clear email below
  }

  try {
    const gridComp = formio.getComponent?.(layout.gridKey);
    if (gridComp) {
      const rowComps = gridComp.rows?.[rowIndex];
      if (rowComps) {
        const comps = Array.isArray(rowComps)
          ? rowComps
          : Object.values(rowComps);
        const emailComp =
          (Array.isArray(rowComps)
            ? rowComps.find(
                (c) => (c?.component?.key || c?.key) === layout.emailKey
              )
            : rowComps[layout.emailKey]) ||
          comps.find(
            (c) => (c?.component?.key || c?.key) === layout.emailKey
          );

        if (emailComp && typeof emailComp.setValue === "function") {
          // Keep calculateValue from wiping this fill — patch live instance too.
          if (emailComp.component) {
            emailComp.component.allowCalculateOverride = true;
            const prev = String(emailComp.component.calculateValue || "");
            if (prev.includes("value = __fromObj ||")) {
              emailComp.component.calculateValue = prev.replace(
                /value = __fromObj \|\|[\s\S]*?;/,
                `var __found = __fromObj || (__sel && (__byId[__sel] || __byLabel[__sel])) || '';
    if (__found) { value = __found; } else if (!__sel) { value = ''; }`
              );
            }
          }
          emailComp.setValue(email, { modified: true, noUpdateEvent: false });
          if (emailComp.data && typeof emailComp.data === "object") {
            emailComp.data[layout.emailKey] = email;
          }
          if (typeof emailComp.redraw === "function") {
            try {
              emailComp.redraw();
            } catch (_) {
              /* ignore */
            }
          }
        }
      }
    }
  } catch (err) {
    console.warn("forceFillCaddieRowEmail:", err);
  }

  // Always also poke the visible input — Form.io datagrid textfields often lag.
  fillCaddieEmailDomInput(formio, layout, rowIndex, email);
}

/**
 * Bind a Form.io-level change listener so Caddie Email fills even when React
 * onChange timing misses the select event. getCaddiesById may be a Map or () => Map.
 */
export function attachCaddieEmailAutoFill(formio, getCaddiesById) {
  if (!formio || typeof formio.on !== "function") return;
  if (formio.__igfCaddieEmailAutoFill) return;
  formio.__igfCaddieEmailAutoFill = true;

  formio.on("change", (changed) => {
    try {
      if (!changed || formio.__igfCaddieEmailFilling) return;
      let layout = getCaddieDatagridLayout();
      if (!layout?.gridKey && formio?.form?.components) {
        layout = ensureCaddieDatagridLayoutFromSchema(formio.form.components);
      }
      if (!layout?.selectKey || !layout?.emailKey) return;
      if (
        !isGridCaddieSelectChange(
          layout.selectKey,
          changed,
          layout,
          formio.submission?.data
        )
      ) {
        return;
      }

      const mapRaw =
        typeof getCaddiesById === "function"
          ? getCaddiesById()
          : getCaddiesById;
      const map =
        mapRaw instanceof Map ? mapRaw : buildCaddiesById(mapRaw || []);

      const data = JSON.parse(
        JSON.stringify(formio.submission?.data || {})
      );
      formio.__igfCaddieEmailFilling = true;
      const next = applyCaddieSelectionToData(
        data,
        map,
        layout.selectKey,
        changed
      );
      pushFormioSubmissionData(formio, next, { gridOnly: true });
      forceFillCaddieRowEmail(formio, next, layout, changed);
    } catch (err) {
      console.warn("attachCaddieEmailAutoFill:", err);
    } finally {
      formio.__igfCaddieEmailFilling = false;
    }
  });
}

/** Clears in-memory caddie list cache (e.g. after CRM role changes). */
export function clearCaddieOptionsCache() {
  caddiesListCache = { base: "", data: null, fetchedAt: 0 };
}

/**
 * Inject CRM caddies into top-level and per-row datagrid caddie dropdowns.
 * Form Builder "values" JSON is replaced at runtime — options come from the API + saved ids.
 */
export function injectCaddieSelectValues(components, caddies, submissionData = null) {
  if (!Array.isArray(components)) return;

  ensureCaddieDatagridLayoutFromSchema(components);
  const layout = getCaddieDatagridLayout();
  const mergedCaddies = mergeKnownCaddiesIntoOptionsList(caddies, submissionData);

  traverseComponents(components, (comp) => {
    const type = (comp.type || "").toLowerCase();
    if (
      layout?.gridKey &&
      (type === "datagrid" || type === "editgrid") &&
      comp.key === layout.gridKey
    ) {
      let defaultRadioConfigured = false;
      (comp.components || []).forEach((col) => {
        if (isDatagridCaddieSelectColumn(col, comp)) {
          fillCaddieSelectComponent(col, mergedCaddies, submissionData);
          return;
        }
        const isDefaultRadio =
          isDefaultCaddieRadioColumn(col, comp) ||
          (!defaultRadioConfigured &&
            (col.type === "radio" || col.type === "checkbox"));
        if (isDefaultRadio) {
          ensureDefaultCaddieRadioRendersInDatagrid(col);
          defaultRadioConfigured = true;
        }
      });
      wireCaddieEmailCalculateValue(comp, mergedCaddies, layout);
      return;
    }

    if (!isCaddieSelectComponent(comp)) return;
    if (layout?.selectKey && comp.key === layout.selectKey) return;
    registeredCaddieSelectKeys.add(comp.key);
    fillCaddieSelectComponent(comp, mergedCaddies, submissionData);
  });
}

/** Prefer primary email, then email2 (CRM often only has one filled). */
function resolveCaddieEmail(person) {
  if (!person || typeof person !== "object") return "";
  return String(
    person.email ||
      person.email2 ||
      person.Email ||
      person.caddieEmail ||
      ""
  ).trim();
}

/** Session cache: knownCaddies emails survive dataGrid → row conversion. */
const knownCaddieEmailById = new Map();

function rememberKnownCaddieEmails(records) {
  (records || []).forEach((entry) => {
    if (!entry || typeof entry !== "object") return;
    const id = extractCaddieIdForStorage(entry._id ?? entry.id ?? entry.value);
    const email = resolveCaddieEmail(entry);
    if (id && email) knownCaddieEmailById.set(id, email);
  });
}

/** CRM person email, else knownCaddies / row cache / live submission. */
function lookupCaddieEmail(personOrId, data = null) {
  if (personOrId && typeof personOrId === "object" && !Array.isArray(personOrId)) {
    const fromPerson = resolveCaddieEmail(personOrId);
    if (fromPerson) return fromPerson;
    const id = extractCaddieIdForStorage(
      personOrId._id ?? personOrId.value ?? personOrId.id
    );
    if (id && knownCaddieEmailById.has(id)) return knownCaddieEmailById.get(id);
    if (data && id) {
      const known = collectKnownCaddieRecords(data).find(
        (kc) => String(kc._id) === id
      );
      const fromKnown = resolveCaddieEmail(known);
      if (fromKnown) {
        knownCaddieEmailById.set(id, fromKnown);
        return fromKnown;
      }
    }
    return "";
  }

  const id = extractCaddieIdForStorage(personOrId);
  if (!id) return "";
  if (knownCaddieEmailById.has(id)) return knownCaddieEmailById.get(id);
  if (data) {
    const known = collectKnownCaddieRecords(data).find(
      (kc) => String(kc._id) === id
    );
    const fromKnown = resolveCaddieEmail(known);
    if (fromKnown) {
      knownCaddieEmailById.set(id, fromKnown);
      return fromKnown;
    }
  }
  return "";
}

function buildDatagridRow(person, layout) {
  const name = person.name || "";
  const lname = person.lname || "";
  const email = lookupCaddieEmail(person);
  const phone = person.phone || person.phone1 || person.phone2 || "";
  const row = {};

  if (layout.mode === "caddieSelectName") {
    row[layout.emailKey] = email;
    if (layout.phoneKey) row[layout.phoneKey] = phone;
    if (layout.defaultRadioKey) row[layout.defaultRadioKey] = DEFAULT_RADIO_OFF;
    return row;
  }

  if (layout.mode === "fullName") {
    row[layout.fullNameKey] = `${name} ${lname}`.trim();
    row[layout.emailKey] = email;
    if (layout.phoneKey) row[layout.phoneKey] = phone;
    return row;
  }

  row[layout.firstNameKey] = name;
  if (layout.lastNameKey) row[layout.lastNameKey] = lname;
  row[layout.emailKey] = email;
  if (layout.phoneKey) row[layout.phoneKey] = phone;
  return row;
}

function getChangedSelectValue(changed) {
  if (!changed) return null;
  const inst = changed.instance || changed.component;
  let raw =
    changed.value ??
    changed.data?.value ??
    (typeof changed.data === "string" || typeof changed.data === "number"
      ? changed.data
      : null) ??
    inst?.dataValue ??
    inst?.value;
  if (raw != null && typeof raw === "object" && !Array.isArray(raw)) {
    raw = raw.value ?? raw._id ?? raw.id ?? null;
  }
  if (raw == null || raw === "") return null;
  return raw;
}

/** Datagrid row select often reports key "select"; path includes dataGrid[n].select. */
function resolveCaddieChangedKey(changed, layout) {
  const key = changed?.component?.key;
  if (!layout?.selectKey) return key;
  if (key === layout.selectKey) return layout.selectKey;

  const paths = [
    changed?.instance?.path,
    changed?.instance?.dataPath,
    changed?.data?.path,
    changed?.component?.path,
  ].filter(Boolean);

  const gridKey = layout.gridKey;
  const selectKey = layout.selectKey;
  for (const p of paths) {
    const s = String(p);
    if (s.includes(`${gridKey}[`) && s.includes(selectKey)) return selectKey;
    if (s.endsWith(`.${selectKey}`) || s === selectKey) return selectKey;
  }
  return key;
}

function isDatagridRowEmpty(row, layout) {
  if (!row || typeof row !== "object") return true;
  if (layout.selectKey && normalizeSelectedIds(row[layout.selectKey]).length) {
    return false;
  }
  if (layout.mode === "caddieSelectName") {
    return !String(row[layout.emailKey] || "").trim();
  }
  const hasName =
    layout.mode === "fullName"
      ? String(row[layout.fullNameKey] || "").trim()
      : String(row[layout.firstNameKey] || "").trim();
  const hasEmail = String(row[layout.emailKey] || "").trim();
  return !hasName && !hasEmail;
}

function resolveDatagridRowIndex(changed, layout, data) {
  if (!changed || !layout?.gridKey) return -1;

  const inst = changed.instance || changed.component;
  if (inst) {
    if (typeof inst.rowIndex === "number") return inst.rowIndex;
    if (typeof inst.row === "number") return inst.row;
    let node = inst.parent;
    while (node) {
      if (typeof node.rowIndex === "number") return node.rowIndex;
      if (typeof node.row === "number") return node.row;
      node = node.parent;
    }
  }
  if (typeof changed.component?.rowIndex === "number") return changed.component.rowIndex;
  if (typeof changed.component?.row === "number") return changed.component.row;

  const paths = [
    inst?.path,
    inst?.dataPath,
    changed.data?.path,
    changed.component?.path,
  ].filter(Boolean);

  const radioKey = layout.defaultRadioKey;
  if (radioKey && changed?.component?.key === radioKey) {
    const reRadio = new RegExp(`${layout.gridKey}\\[(\\d+)\\]`, "i");
    for (const p of paths) {
      const m = String(p).match(reRadio);
      if (m) return parseInt(m[1], 10);
    }
  }

  if (radioKey) {
    for (const p of paths) {
      const m = String(p).match(
        new RegExp(`${layout.gridKey}\\[(\\d+)\\]\\.?${radioKey}`, "i")
      );
      if (m) return parseInt(m[1], 10);
    }
  }

  const re = new RegExp(`${layout.gridKey}\\[(\\d+)\\]`, "i");
  for (const p of paths) {
    const m = String(p).match(re);
    if (m) return parseInt(m[1], 10);
  }
  for (const p of paths) {
    const m = String(p).match(/\[(\d+)\]/);
    if (m) return parseInt(m[1], 10);
  }

  const grid = data?.[layout.gridKey];
  const changedId = normalizeSelectedIds(getChangedSelectValue(changed))[0];

  if (changedId && Array.isArray(grid)) {
    for (let i = 0; i < grid.length; i++) {
      if (normalizeSelectedIds(grid[i]?.[layout.selectKey])[0] === changedId) {
        return i;
      }
    }
    const emptyIdx = grid.findIndex((r) => isDatagridRowEmpty(r, layout));
    if (emptyIdx >= 0) return emptyIdx;
    if (grid.length === 1) return 0;
    if (grid.length > 0) return grid.length - 1;
  }

  if (Array.isArray(grid) && grid.length > 0) return 0;

  return -1;
}

function isGridCaddieSelectChange(changedKey, changed, layout, data) {
  if (!layout?.selectKey || !layout?.gridKey) return false;
  const changedCompKey = changed?.component?.key;
  // Only the caddie name dropdown — not radio / email / remove.
  if (
    changedCompKey &&
    changedCompKey !== layout.selectKey &&
    !isCaddieSelectComponent(changed?.component)
  ) {
    return false;
  }
  if (isDatagridCaddieChange(changed, layout)) {
    if (
      changedCompKey === layout.emailKey ||
      changedCompKey === layout.defaultRadioKey ||
      changedCompKey === layout.phoneKey
    ) {
      return false;
    }
    return (
      !changedCompKey ||
      changedCompKey === layout.selectKey ||
      isCaddieSelectComponent(changed?.component)
    );
  }
  if (changedKey !== layout.selectKey) return false;
  if (resolveDatagridRowIndex(changed, layout, data) >= 0) return true;
  return getChangedSelectValue(changed) != null;
}

const ATHLETE_IDENTITY_KEYS = new Set([
  "givenFirstName",
  "familyLastName",
  "emailAddress",
  "email",
  "personId",
]);

function applyPersonToFlatFields(data, person, selectKey) {
  const name = person.name || "";
  const lname = person.lname || "";
  const email = resolveCaddieEmail(person);

  const mapped = resolveCaddieFieldMap(selectKey);
  const keysWritten = [];
  if (mapped) {
    if (!ATHLETE_IDENTITY_KEYS.has(mapped.firstName)) {
      data[mapped.firstName] = name;
      keysWritten.push(mapped.firstName);
    }
    if (!ATHLETE_IDENTITY_KEYS.has(mapped.lastName)) {
      data[mapped.lastName] = lname;
      keysWritten.push(mapped.lastName);
    }
    if (!ATHLETE_IDENTITY_KEYS.has(mapped.email)) {
      data[mapped.email] = email;
      keysWritten.push(mapped.email);
    }
  }

  const flatAssignments = [
    ["givenFirstName1", name],
    ["caddieLastName", lname],
    ["familyLastName1", email],
    ["caddieFirstName", name],
    ["caddieEmail", email],
    ["caddieLastName1", lname],
    ["caddieFirstName1", name],
  ];
  flatAssignments.forEach(([key, val]) => {
    if (ATHLETE_IDENTITY_KEYS.has(key)) return;
    data[key] = val;
    keysWritten.push(key);
  });
}

/** Resolve CRM caddie from select value (ObjectId or display label). */
function resolveCaddiePerson(map, selectVal) {
  if (!map || !(map instanceof Map)) return null;
  const ids = normalizeSelectedIds(selectVal);
  if (ids[0] && map.has(ids[0])) {
    return { person: map.get(ids[0]), id: ids[0] };
  }

  let label = "";
  if (selectVal != null && typeof selectVal === "object" && !Array.isArray(selectVal)) {
    label = String(selectVal.label || selectVal.value || "").trim();
  } else if (selectVal != null) {
    label = String(selectVal).trim();
  }
  if (!label) return null;

  const needle = label.toLowerCase();
  for (const person of map.values()) {
    const names = [
      person.label,
      formatCaddieDisplayName(person),
      `${person.name || ""} ${person.lname || ""}`.trim(),
    ]
      .filter(Boolean)
      .map((n) => String(n).trim().toLowerCase());
    if (names.includes(needle)) {
      return { person, id: String(person._id) };
    }
  }
  return null;
}

/** Prefer CRM map; fall back to email stored on the Choices/select option. */
function emailFromSelectChange(changed, person) {
  const fromPerson = lookupCaddieEmail(person);
  if (fromPerson) return fromPerson;

  if (changed?.value && typeof changed.value === "object") {
    const fromVal = String(
      changed.value.email ||
        changed.value.customProperties?.email ||
        ""
    ).trim();
    if (fromVal) return fromVal;
  }

  const inst = changed?.instance;
  const selectedId = normalizeSelectedIds(getChangedSelectValue(changed))[0];
  if (selectedId) {
    const cached = lookupCaddieEmail(selectedId);
    if (cached) return cached;
  }
  if (!inst || !selectedId) return "";

  const pools = [
    inst.selectOptions,
    inst.downloadedResources,
    inst.component?.data?.values,
    inst.choices?.config?.choices,
  ];
  for (const pool of pools) {
    if (!Array.isArray(pool)) continue;
    const match = pool.find(
      (item) =>
        String(item?.value ?? item?.id ?? item?._id ?? "") === selectedId ||
        String(item?.label || "").trim() === selectedId
    );
    const email =
      resolveCaddieEmail(match) ||
      String(match?.email || match?.customProperties?.email || "").trim();
    if (email) return email;
  }
  return "";
}

/** Fill one datagrid row from its row dropdown; other rows (manual entry) are left as-is. */
function applyPersonToGridRow(data, caddiesById, layout, rowIndex, changed) {
  const map =
    caddiesById instanceof Map ? caddiesById : buildCaddiesById(caddiesById);
  const grid = Array.isArray(data[layout.gridKey])
    ? data[layout.gridKey].map((r) => ({ ...r }))
    : [];

  const safeIndex = rowIndex >= 0 ? rowIndex : 0;
  while (grid.length <= safeIndex) grid.push({});

  const row = { ...(grid[safeIndex] || {}) };
  const fromChange = getChangedSelectValue(changed);
  let selectVal =
    fromChange != null
      ? fromChange
      : layout.selectKey
        ? row[layout.selectKey]
        : null;

  const resolved = resolveCaddiePerson(map, selectVal);
  if (!resolved?.person) {
    const fallbackEmail = emailFromSelectChange(changed, null);
    if (fallbackEmail && layout.emailKey) {
      row[layout.emailKey] = fallbackEmail;
      if (layout.selectKey && selectVal != null) row[layout.selectKey] = selectVal;
      grid[safeIndex] = row;
      data[layout.gridKey] = grid;
    } else {
      data[layout.gridKey] = grid;
    }
    return data;
  }

  const filled = buildDatagridRow(resolved.person, layout);
  const optionEmail = emailFromSelectChange(changed, resolved.person);
  const knownEmail = lookupCaddieEmail(resolved.id || resolved.person, data);
  const email = optionEmail || knownEmail || "";
  if (layout.emailKey && email) {
    filled[layout.emailKey] = email;
    knownCaddieEmailById.set(String(resolved.id), email);
  } else if (layout.emailKey) {
    // Never blank out an email already typed / restored on the row.
    const prev = String(row[layout.emailKey] || "").trim();
    if (prev) filled[layout.emailKey] = prev;
  }
  if (layout.selectKey) {
    filled[layout.selectKey] = selectVal ?? resolved.id;
  }
  grid[safeIndex] = { ...row, ...filled };
  data[layout.gridKey] = grid;
  return ensureDefaultCaddieIfNone(data, layout, safeIndex);
}

/** Add/update grid rows for top-level caddie picks without removing manual rows. */
function mergeTopLevelCaddiesIntoGrid(data, persons, layout) {
  if (!layout?.gridKey || !persons?.length) return;

  const grid = Array.isArray(data[layout.gridKey])
    ? data[layout.gridKey].map((r) => ({ ...r }))
    : [];

  const rowHasCaddieId = (row, id) => {
    if (!layout.selectKey) return false;
    return normalizeSelectedIds(row[layout.selectKey])[0] === id;
  };

  persons.forEach((person) => {
    const id = String(person._id);
    const newRow = buildDatagridRow(person, layout);
    if (layout.selectKey) newRow[layout.selectKey] = id;

    const existingIdx = grid.findIndex((r) => rowHasCaddieId(r, id));
    if (existingIdx >= 0) {
      grid[existingIdx] = { ...grid[existingIdx], ...newRow };
      return;
    }

    const emptyIdx = grid.findIndex((r) => isDatagridRowEmpty(r, layout));
    if (emptyIdx >= 0) {
      grid[emptyIdx] = { ...grid[emptyIdx], ...newRow };
    } else {
      grid.push(newRow);
    }
  });

  data[layout.gridKey] = grid;
}

/** Push row values into live datagrid fields so name/email show immediately after select. */
function syncCaddieGridToFormio(formio, data, layout) {
  if (!formio || !layout?.gridKey || !data) return;
  const gridData = data[layout.gridKey];
  if (!Array.isArray(gridData)) return;

  try {
    const gridComp = formio.getComponent(layout.gridKey);
    if (!gridComp) return;

    const rows = JSON.parse(JSON.stringify(gridData));

    if (typeof gridComp.setValue === "function") {
      gridComp.setValue(rows, { noUpdateEvent: true });
    }

    const fieldKeys = [
      layout.fullNameKey,
      layout.firstNameKey,
      layout.lastNameKey,
      layout.emailKey,
      layout.phoneKey,
      layout.selectKey,
      layout.defaultRadioKey,
    ].filter(Boolean);

    if (Array.isArray(gridComp.rows)) {
      rows.forEach((row, rowIndex) => {
        if (typeof gridComp.setRowComponentsData === "function") {
          gridComp.setRowComponentsData(rowIndex, row);
        }
        const rowComps = gridComp.rows[rowIndex];
        if (!rowComps || typeof rowComps !== "object") return;
        const comps = Array.isArray(rowComps)
          ? rowComps
          : Object.values(rowComps);
        comps.forEach((comp) => {
          const key = comp?.component?.key || comp?.key;
          if (!key || row[key] === undefined) return;
          if (!fieldKeys.includes(key) && key !== layout.emailKey) return;
          if (typeof comp.setValue === "function") {
            comp.setValue(row[key], { noUpdateEvent: true });
          }
        });
        // Explicit email fill — Choices/select redraw can leave the textfield stale.
        if (layout.emailKey && row[layout.emailKey] !== undefined) {
          const emailComp =
            (Array.isArray(rowComps)
              ? rowComps.find((c) => (c?.component?.key || c?.key) === layout.emailKey)
              : rowComps[layout.emailKey]) ||
            comps.find((c) => (c?.component?.key || c?.key) === layout.emailKey);
          if (emailComp && typeof emailComp.setValue === "function") {
            emailComp.setValue(row[layout.emailKey], { noUpdateEvent: true });
          }
        }
      });
    }

    if (typeof gridComp.restoreComponentsContext === "function") {
      gridComp.restoreComponentsContext();
    }
  } catch (err) {
    console.warn("syncCaddieGridToFormio:", err);
  }
}

/** Resolve live Form.io webform from react-formio ref and/or stored instance. */
export function resolveFormioWebform(...candidates) {
  for (const ref of candidates) {
    if (!ref) continue;
    if (
      typeof ref.getComponent === "function" ||
      typeof ref.everyComponent === "function"
    ) {
      return ref;
    }
    const inner = ref.formio;
    if (
      inner &&
      (typeof inner.getComponent === "function" ||
        typeof inner.everyComponent === "function")
    ) {
      return inner;
    }
  }
  return null;
}

/** Uncheck all default radios in the grid except one row (when webform ref is missing). */
export function enforceExclusiveDefaultRadioDom(container, data, layout = null) {
  const layoutRef = layout || getCaddieDatagridLayout();
  if (!container || !layoutRef?.gridKey || !data) return;
  const grid = data[layoutRef.gridKey];
  if (!Array.isArray(grid)) return;
  const selectedIdx = findLastDefaultRadioRowIndex(grid, layoutRef);
  if (selectedIdx < 0) return;

  const gridRoot =
    container.querySelector(`.formio-component-${layoutRef.gridKey}`) ||
    container;
  const rowEls = Array.from(
    gridRoot.querySelectorAll(".datagrid-row, .editgrid-row, tbody tr")
  ).filter((el) => el.querySelector('input[type="radio"]'));
  rowEls.forEach((rowEl, i) => {
    rowEl.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = i === selectedIdx;
    });
  });
}

/** Update only default-radio fields so switching rows behaves like one radio group. */
function syncExclusiveDefaultRadioToFormio(formio, data, layout) {
  if (!formio || !layout?.gridKey || !layout?.defaultRadioKey || !data) return;
  const grid = data[layout.gridKey];
  if (!Array.isArray(grid)) return;

  try {
    const gridComp = formio.getComponent?.(layout.gridKey);
    if (!gridComp) return;

    const radioKey = layout.defaultRadioKey;
    const activeVal = layout.defaultRadioActiveValue;
    const offVal = DEFAULT_RADIO_OFF;
    const rowsCopy = grid.map((row) => ({ ...row }));

    const selectedIdx = findLastDefaultRadioRowIndex(rowsCopy, layout);

    rowsCopy.forEach((row, rowIndex) => {
      const val =
        selectedIdx >= 0 && rowIndex === selectedIdx ? activeVal : offVal;
      const rowComps = gridComp.rows?.[rowIndex];
      if (!rowComps || typeof rowComps !== "object") return;
      const comps = Array.isArray(rowComps) ? rowComps : Object.values(rowComps);
      comps.forEach((comp) => {
        const key = comp?.component?.key || comp?.key;
        if (key !== radioKey || typeof comp.setValue !== "function") return;
        comp.setValue(val, { noUpdateEvent: true });
      });
    });

    if (gridComp.element && selectedIdx >= 0) {
      enforceExclusiveDefaultRadioDom(gridComp.element, data, layout);
    }

    if (formio.submission?.data) {
      formio.submission.data[layout.gridKey] = rowsCopy;
      if (Array.isArray(data[PRESENT_CADDIES_KEY])) {
        formio.submission.data[PRESENT_CADDIES_KEY] = data[PRESENT_CADDIES_KEY];
      }
    }
  } catch (err) {
    console.warn("syncExclusiveDefaultRadioToFormio:", err);
  }
}

/**
 * Convert dataGrid.knownCaddies (+ presentCaddies for default radio) to Form.io rows.
 */
export function normalizeDataGridForFormioRender(data, options = {}) {
  if (!data || typeof data !== "object") return data;
  const { forceRestore = false } = options;
  const CADDIE_GRID_KEYS_NORM = ["dataGrid", "caddieGrid", "dataGrid1"];
  const layout = resolveLayoutForCaddieRestore(data);
  const primaryGridKey = layout?.gridKey || "dataGrid";

  hoistCaddieArraysToPersonDataRoot(data, primaryGridKey);

  CADDIE_GRID_KEYS_NORM.forEach((gridKey) => {
    const g = data[gridKey];
    if (
      !forceRestore &&
      Array.isArray(g) &&
      g.length > 0 &&
      g.some((row) => isCaddieGridRowForStorage(row))
    ) {
      return;
    }
    restoreStoredCaddieGridForRender(data, layout, gridKey);
  });

  return reconcileDefaultCaddieDisplay(data, layout, null);
}

const CADDIE_STORAGE_SELECT_KEYS = ["caddies", "caddie", "select"];
const CADDIE_STORAGE_GRID_KEYS = ["dataGrid", "caddieGrid", "dataGrid1"];
const CADDIE_FLAT_KEYS_TO_STRIP = [
  "caddies",
  "caddie",
  "givenFirstName1",
  "caddieLastName",
  "familyLastName1",
  "caddieFirstName",
  "caddieEmail",
  "caddieLastName1",
  "caddieFirstName1",
];

function extractCaddieIdForStorage(raw) {
  if (raw == null || raw === "") return "";
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return String(raw.value ?? raw._id ?? raw.id ?? "").trim();
  }
  return String(raw).trim();
}

function isObjectIdStr(s) {
  return /^[a-f\d]{24}$/i.test(String(s || ""));
}

function isCaddieGridRowForStorage(row) {
  if (!row || typeof row !== "object" || Array.isArray(row)) return false;
  return CADDIE_STORAGE_SELECT_KEYS.some((key) =>
    isObjectIdStr(extractCaddieIdForStorage(row[key]))
  );
}

function gridIsCaddieData(grid) {
  if (Array.isArray(grid)) return grid.some(isCaddieGridRowForStorage);
  if (
    grid &&
    typeof grid === "object" &&
    Array.isArray(grid[KNOWN_CADDIES_KEY]) &&
    grid[KNOWN_CADDIES_KEY].length
  ) {
    return true;
  }
  return false;
}

/**
 * Save: dataGrid.knownCaddies (all); person_data.presentCaddies (default record).
 */
export function normalizeCaddiesForStorage(data, caddiesById = null) {
  if (!data || typeof data !== "object") return data;

  const next = JSON.parse(JSON.stringify(data));
  const layout = getCaddieDatagridLayout();
  const selectKey = layout?.selectKey || "caddies";
  const emailKey = layout?.emailKey || "familyLastName4";
  const phoneKey = layout?.phoneKey || "phoneNumber1";

  const hasTopLevel = CADDIE_STORAGE_SELECT_KEYS.some((k) =>
    isObjectIdStr(extractCaddieIdForStorage(next[k]))
  );
  const hasGrid = CADDIE_STORAGE_GRID_KEYS.some((k) => gridIsCaddieData(next[k]));
  const hasPresent =
    Array.isArray(next[PRESENT_CADDIES_KEY]) && next[PRESENT_CADDIES_KEY].length > 0;

  if (!hasTopLevel && !hasGrid && !hasPresent) return next;

  const primaryGridKey =
    layout?.gridKey ||
    CADDIE_STORAGE_GRID_KEYS.find((k) => gridIsCaddieData(next[k])) ||
    "dataGrid";

  const map =
    caddiesById instanceof Map
      ? caddiesById
      : caddiesById
        ? buildCaddiesById(caddiesById)
        : new Map();

  const seen = new Set();
  const orderedIds = [];
  const rowEmailById = new Map();

  const addId = (raw, row) => {
    const idStr = extractCaddieIdForStorage(raw);
    if (!isObjectIdStr(idStr) || seen.has(idStr)) return;
    seen.add(idStr);
    orderedIds.push(idStr);
    const fromRow = row && emailKey ? String(row[emailKey] || "").trim() : "";
    if (fromRow) rowEmailById.set(idStr, fromRow);
    if (row?.email) rowEmailById.set(idStr, String(row.email).trim());
  };

  CADDIE_STORAGE_SELECT_KEYS.forEach((key) => addId(next[key]));

  CADDIE_STORAGE_GRID_KEYS.forEach((gridKey) => {
    const grid = next[gridKey];
    if (Array.isArray(grid)) {
      grid.forEach((row) => {
        CADDIE_STORAGE_SELECT_KEYS.forEach((key) => addId(row?.[key], row));
        addId(row?.[selectKey], row);
      });
    } else if (grid?.[KNOWN_CADDIES_KEY]) {
      grid[KNOWN_CADDIES_KEY].forEach((kc) => addId(kc?._id ?? kc, kc));
    }
  });

  let defaultIdStr = resolveDefaultCaddieIdForStorage(next, layout);
  if (!defaultIdStr && orderedIds.length === 1) {
    defaultIdStr = orderedIds[0];
  }

  const knownCaddies = orderedIds.map((idStr) =>
    buildCaddieStorageRecord(idStr, map, rowEmailById)
  );

  let presentCaddies = [];
  if (defaultIdStr) {
    const defaultRecord =
      knownCaddies.find((kc) => String(kc._id) === defaultIdStr) ||
      buildCaddieStorageRecord(defaultIdStr, map, rowEmailById);
    if (isObjectIdStr(extractCaddieIdForStorage(defaultRecord._id))) {
      presentCaddies = [
        {
          _id: defaultRecord._id,
          name: defaultRecord.name,
          lname: defaultRecord.lname,
          email: defaultRecord.email,
        },
      ];
    }
  }

  next[primaryGridKey] = { [KNOWN_CADDIES_KEY]: knownCaddies };
  next[PRESENT_CADDIES_KEY] = presentCaddies;
  next[ADDITIONAL_CADDIES_KEY] = getAdditionalCaddiesList(next, layout);
  delete next.defaultCaddieId;

  hoistCaddieArraysToPersonDataRoot(next, primaryGridKey);

  CADDIE_STORAGE_GRID_KEYS.forEach((gridKey) => {
    if (gridKey !== primaryGridKey && gridIsCaddieData(next[gridKey])) {
      delete next[gridKey];
    }
  });

  CADDIE_FLAT_KEYS_TO_STRIP.forEach((key) => delete next[key]);
  if (emailKey) delete next[emailKey];
  if (phoneKey) delete next[phoneKey];
  if (layout?.defaultRadioKey) delete next[layout.defaultRadioKey];

  return next;
}

/** Whether person_data still has stored or live caddie picks / default. */
export function personDataHasCaddieFields(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return false;
  if (Array.isArray(data[PRESENT_CADDIES_KEY]) && data[PRESENT_CADDIES_KEY].length) {
    return true;
  }
  return CADDIE_STORAGE_GRID_KEYS.some((k) => gridIsCaddieData(data[k]));
}

export function copyCaddieFieldsFromSource(target, source) {
  if (!target || !source || typeof source !== "object") return target;
  if (Array.isArray(source[PRESENT_CADDIES_KEY]) && source[PRESENT_CADDIES_KEY].length) {
    target[PRESENT_CADDIES_KEY] = JSON.parse(
      JSON.stringify(source[PRESENT_CADDIES_KEY])
    );
  }
  if (source[ADDITIONAL_CADDIES_KEY] != null) {
    target[ADDITIONAL_CADDIES_KEY] = JSON.parse(
      JSON.stringify(source[ADDITIONAL_CADDIES_KEY])
    );
  }
  CADDIE_STORAGE_GRID_KEYS.forEach((gridKey) => {
    if (gridIsCaddieData(source[gridKey])) {
      target[gridKey] = JSON.parse(JSON.stringify(source[gridKey]));
    }
  });
  return target;
}

/**
 * Every participant save (any tab): keep knownCaddies + presentCaddies unless
 * REGISTRATION supplied a live grid update.
 */
export function preserveAndNormalizeCaddiesOnSave(
  merged,
  savedBaseline,
  options = {}
) {
  const {
    caddiesById = null,
    formio = null,
    isRegistrationTab = false,
    liveSnapshot = null,
    tabLabel = "",
  } = options;

  if (!merged || typeof merged !== "object") return merged;

  const savedHas = personDataHasCaddieFields(savedBaseline);
  const mergedHas = personDataHasCaddieFields(merged);
  const liveHas = personDataHasCaddieFields(liveSnapshot);

  if (!savedHas && !mergedHas && !liveHas) return merged;

  let next = JSON.parse(JSON.stringify(merged));
  const layout = getCaddieDatagridLayout();
  const gridKey = layout?.gridKey || "dataGrid";
  const liveGridIsCaddieRows =
    Array.isArray(next[gridKey]) &&
    next[gridKey].some((row) => isCaddieGridRowForStorage(row));
  const registrationLiveUpdate =
    isRegistrationTab && (liveGridIsCaddieRows || Boolean(formio));

  if (registrationLiveUpdate) {
    if (formio) {
      next = finalizeCaddieGridForSubmit(next, caddiesById, formio);
    } else if (liveHas) {
      copyCaddieFieldsFromSource(next, liveSnapshot);
      next = normalizeDataGridForFormioRender(next);
      reconcileDefaultCaddieDisplay(next, layout, caddiesById);
    }
    const out = normalizeCaddiesForStorage(next, caddiesById);
    return out;
  }

  if (savedHas && savedBaseline) {
    copyCaddieFieldsFromSource(next, savedBaseline);
  } else if (liveHas && liveSnapshot) {
    copyCaddieFieldsFromSource(next, liveSnapshot);
  }

  if (!personDataHasCaddieFields(next) && savedHas) {
    copyCaddieFieldsFromSource(next, savedBaseline);
  }

  const out = normalizeCaddiesForStorage(next, caddiesById);
  return out;
}

function restoreStoredCaddieGridForRender(data, layout, gridKey) {
  if (!data || !gridKey || !layout?.selectKey) return;
  const grid = data[gridKey];

  const presentList = getPresentCaddiesList(data, layout);
  let defaultIdStr = resolveDefaultCaddieFromLiveGrid(data, layout);
  if (!defaultIdStr) {
    defaultIdStr = presentList.length
      ? extractCaddieIdForStorage(presentList[0]?._id ?? presentList[0])
      : "";
  }
  if (!defaultIdStr) {
    defaultIdStr = resolveDefaultCaddieIdFromGrid(data, layout);
  }

  // Prefer nested dataGrid.knownCaddies, then root knownCaddies (guest/admin lifts).
  let knownList = [];
  if (grid && !Array.isArray(grid) && typeof grid === "object") {
    if (Array.isArray(grid[KNOWN_CADDIES_KEY]) && grid[KNOWN_CADDIES_KEY].length) {
      knownList = grid[KNOWN_CADDIES_KEY];
    }
  }
  if (!knownList.length && Array.isArray(data[KNOWN_CADDIES_KEY])) {
    knownList = data[KNOWN_CADDIES_KEY];
  }

  if (knownList.length) {
    // Keep a root copy so email lookup still works after dataGrid becomes rows.
    data[KNOWN_CADDIES_KEY] = knownList.map((kc) => {
      if (!kc || typeof kc !== "object") return kc;
      const idStr = extractCaddieIdForStorage(kc._id ?? kc);
      return {
        ...kc,
        _id: idStr || kc._id,
        email: resolveCaddieEmail(kc),
      };
    });
    rememberKnownCaddieEmails(data[KNOWN_CADDIES_KEY]);
    const rows = knownList
      .map((kc) => buildKnownCaddieRowForRender(kc, layout, defaultIdStr))
      .filter(Boolean);
    data[gridKey] = dedupeGridRowsByCaddieId(rows, layout);
    return;
  }

  // Object-shaped empty grid with only presentCaddies (already hoisted or nested).
  if (grid && !Array.isArray(grid) && typeof grid === "object" && presentList.length) {
    data[gridKey] = presentList
      .map((entry) => buildKnownCaddieRowForRender(entry, layout, defaultIdStr))
      .filter(Boolean);
    return;
  }

  if ((!grid || (Array.isArray(grid) && grid.length === 0)) && presentList.length) {
    data[gridKey] = presentList
      .map((entry) => buildKnownCaddieRowForRender(entry, layout, defaultIdStr))
      .filter(Boolean);
  }
}

function restoreKnownCaddiesToSelectKey(data, layout) {
  if (!layout?.gridKey) return;
  const grid = data[layout.gridKey];
  if (Array.isArray(grid) && grid.some((row) => isCaddieGridRowForStorage(row))) {
    return;
  }
  restoreStoredCaddieGridForRender(data, layout, layout.gridKey);
}

/** Rows with a caddie id in the dropdown get name/email from CRM. */
export function prefillCaddieDatagridRows(data, caddiesById) {
  if (!data) return data;
  const layout = getCaddieDatagridLayout();
  if (!layout?.gridKey || !layout.selectKey) return data;

  // Restore knownCaddies → select key so Formio can match the saved caddie to its option
  restoreKnownCaddiesToSelectKey(data, layout);

  const map =
    caddiesById instanceof Map ? caddiesById : buildCaddiesById(caddiesById);
  const grid = Array.isArray(data[layout.gridKey])
    ? data[layout.gridKey].map((r) => ({ ...r }))
    : [];

  let touched = false;
  grid.forEach((row, i) => {
    const resolved = resolveCaddiePerson(map, row[layout.selectKey]);
    if (!resolved?.person) return;
    const filled = buildDatagridRow(resolved.person, layout);
    filled[layout.selectKey] = row[layout.selectKey] ?? resolved.id;
    if (layout.emailKey) {
      const personEmail = lookupCaddieEmail(resolved.person, data);
      const rowEmail = String(row[layout.emailKey] || "").trim();
      // Never overwrite a saved knownCaddies / row email with empty CRM email.
      filled[layout.emailKey] = personEmail || rowEmail || "";
      if (filled[layout.emailKey] && resolved.id) {
        knownCaddieEmailById.set(String(resolved.id), filled[layout.emailKey]);
      }
    }
    if (layout.defaultRadioKey) {
      const prevRadio = row[layout.defaultRadioKey];
      if (
        prevRadio != null &&
        prevRadio !== "" &&
        prevRadio !== DEFAULT_RADIO_OFF &&
        isRowDefaultRadioOn({ [layout.defaultRadioKey]: prevRadio }, layout)
      ) {
        filled[layout.defaultRadioKey] = prevRadio;
      }
    }
    grid[i] = { ...row, ...filled };
    touched = true;
  });

  if (touched) data[layout.gridKey] = grid;
  return sanitizeGridDefaultRadioValues(data, layout);
}

export function pushFormioSubmissionData(formio, data, options = {}) {
  if (!formio || !data) return;
  const { gridOnly = false, radioOnly = false } = options;
  try {
    const layout = getCaddieDatagridLayout();
    sanitizeGridDefaultRadioValues(data, layout);

    if (formio.submission) {
      formio.submission.data = data;
    }
    if (radioOnly && layout?.gridKey && Array.isArray(data[layout.gridKey])) {
      syncExclusiveDefaultRadioToFormio(formio, data, layout);
      return;
    }
    if (layout?.gridKey && Array.isArray(data[layout.gridKey])) {
      syncCaddieGridToFormio(formio, data, layout);
      if (gridOnly) return;
    }

    if (typeof formio.setSubmission === "function") {
      formio.setSubmission({ data }, { noValidate: true });
    } else if (typeof formio.setValue === "function") {
      formio.setValue({ data }, { noUpdateEvent: true });
    }
  } catch (err) {
    console.warn("pushFormioSubmissionData:", err);
  }
}

/** Push CRM options onto live Form.io select / Choices.js instances. */
function pushCaddieValuesOntoLiveSelects(formio, values) {
  if (!formio || !Array.isArray(values)) return;
  if (typeof formio.everyComponent !== "function") return;

  formio.everyComponent((inst) => {
    const c = inst?.component;
    if (!c || c.type !== "select") return;
    const parentGrid = inst.parent?.component;
    const isGridCaddie =
      parentGrid &&
      (parentGrid.type === "datagrid" || parentGrid.type === "editgrid") &&
      isDatagridCaddieSelectColumn(c, parentGrid);
    if (!isCaddieSelectComponent(c) && !isGridCaddie) return;

    c.dataSrc = "values";
    c.data = { ...(c.data || {}), values };
    if (c.data.url) delete c.data.url;
    c.lazyLoad = false;
    c.valueProperty = "value";
    c.template = c.template || "<span>{{ item.label }}</span>";

    try {
      if (typeof inst.setItems === "function") {
        inst.setItems(values, false);
      } else if (typeof inst.updateItems === "function") {
        inst.updateItems(values, true);
      } else if (typeof inst.loadItems === "function") {
        inst.loadItems(values, [], null, null, null, null, null, null);
      }

      // Choices.js: setChoices keeps the open/dropdown path working after mount.
      const choices = inst.choices || inst.choicesInstance;
      if (choices && typeof choices.setChoices === "function") {
        const choiceItems = values.map((v) => ({
          value: v.value,
          label: v.label,
          selected: false,
          disabled: false,
          customProperties: { email: v.email || "" },
        }));
        if (typeof choices.clearChoices === "function") {
          choices.clearChoices();
        } else if (typeof choices.clearStore === "function") {
          choices.clearStore();
        }
        choices.setChoices(choiceItems, "value", "label", true);
      }
    } catch (_) {
      /* ignore per-instance refresh errors */
    }
  });
}

/** Re-apply CRM caddie dropdown options on the live Form.io instance (after mount). */
export function reinjectCaddieOptionsOnFormio(
  formio,
  caddies,
  submissionData = null,
  { redraw = false } = {}
) {
  if (!formio?.form?.components) return;
  const layout = getCaddieDatagridLayout();
  const liveData = submissionData || formio.submission?.data || null;
  const mergedCaddies = mergeKnownCaddiesIntoOptionsList(
    Array.isArray(caddies) ? caddies : [],
    liveData
  );

  if (formio.submission?.data) {
    sanitizeGridDefaultRadioValues(formio.submission.data, layout);
  }
  if (submissionData) {
    sanitizeGridDefaultRadioValues(submissionData, layout);
  }
  // Schema first — datagrid row templates clone these values on redraw.
  injectCaddieSelectValues(formio.form.components, mergedCaddies, liveData);
  const values = buildCaddieSelectOptions(mergedCaddies, liveData);

  if (layout?.gridKey) {
    try {
      const gridComp = formio.getComponent?.(layout.gridKey);
      if (gridComp?.component?.components && layout.defaultRadioKey) {
        const col = gridComp.component.components.find(
          (c) => c.key === layout.defaultRadioKey
        );
        if (col) ensureDefaultCaddieRadioRendersInDatagrid(col);
      }
      // Redraw first so new row instances pick up injected schema values,
      // then push onto live Choices (do not triggerRedraw per-select — that
      // wiped options after setItems).
      if (gridComp && typeof gridComp.redraw === "function") {
        gridComp.redraw();
      }
    } catch (err) {
      console.warn("reinjectCaddieOptionsOnFormio grid:", err);
    }
  }

  try {
    pushCaddieValuesOntoLiveSelects(formio, values);
  } catch (err) {
    console.warn("reinjectCaddieOptionsOnFormio selects:", err);
  }

  if (redraw && typeof formio.redraw === "function") {
    formio.redraw();
    try {
      pushCaddieValuesOntoLiveSelects(formio, values);
    } catch (_) {
      /* ignore */
    }
  }
}

/** Apply CRM caddie data to submission + refresh datagrid UI (load + after select). */
export function syncCaddieFieldsForFormio(formio, data, caddiesById) {
  if (!formio || !data) return data;
  const next = JSON.parse(JSON.stringify(data));
  const layout = getCaddieDatagridLayout();
  const map =
    caddiesById instanceof Map ? caddiesById : buildCaddiesById(caddiesById);

  const topKeys = new Set([...CADDIE_SELECT_KEYS, ...registeredCaddieSelectKeys]);
  topKeys.forEach((key) => {
    if (!isTopLevelCaddieSelectKey(key)) return;
    const ids = normalizeSelectedIds(next[key]);
    if (!ids.length) return;
    const persons = ids.map((id) => map.get(id)).filter(Boolean);
    if (!persons.length) return;
    applyPersonToFlatFields(next, persons[0], key);
    if (layout?.gridKey) mergeTopLevelCaddiesIntoGrid(next, persons, layout);
  });

  prefillCaddieDatagridRows(next, caddiesById);
  reconcileDefaultCaddieDisplay(next, layout, caddiesById);
  if (formio && layout?.defaultRadioKey) {
    syncExclusiveDefaultRadioToFormio(formio, next, layout);
  }
  pushFormioSubmissionData(formio, next, { gridOnly: true });
  return next;
}

/**
 * Caddie dropdown change: per-row fill (Add Another) or append rows (top-level select).
 * Manual name/email entry in rows without a dropdown selection is preserved.
 */
export function applyCaddieSelectionToData(data, caddiesById, changedKey, changed) {
  if (!data || !changedKey) return data;

  const layout = caddieDatagridLayout;
  const map =
    caddiesById instanceof Map ? caddiesById : buildCaddiesById(caddiesById);

  if (isGridCaddieSelectChange(changedKey, changed, layout, data)) {
    const rowIndex = resolveDatagridRowIndex(changed, layout, data);
    return applyPersonToGridRow(data, map, layout, rowIndex, changed);
  }

  if (!isTopLevelCaddieSelectKey(changedKey, changed, layout)) return data;

  const selectedIds = normalizeSelectedIds(data[changedKey]);
  if (!selectedIds.length) return data;

  const persons = selectedIds
    .map((id) => map.get(String(id)))
    .filter(Boolean);
  if (!persons.length) return data;

  applyPersonToFlatFields(data, persons[0], changedKey);
  if (layout?.gridKey) {
    mergeTopLevelCaddiesIntoGrid(data, persons, layout);
  }
  return data;
}

export function applyCaddieSelectionToFormio(
  formio,
  data,
  caddiesById,
  changedKey,
  changed
) {
  if (!data || !changedKey) return data;
  const layout = getCaddieDatagridLayout();
  const next = applyCaddieSelectionToData(
    JSON.parse(JSON.stringify(data)),
    caddiesById,
    changedKey,
    changed
  );
  const gridOnly = isGridCaddieSelectChange(changedKey, changed, layout, next);
  pushFormioSubmissionData(formio, next, { gridOnly });
  if (gridOnly && formio && layout?.emailKey) {
    forceFillCaddieRowEmail(formio, next, layout, changed);
  }
  return next;
}

export function formSchemaHasCaddieSelect(components) {
  if (!Array.isArray(components)) return false;
  let found = false;
  traverseComponents(components, (comp) => {
    if (isCaddieSelectComponent(comp)) found = true;
    if (comp.type === "datagrid" || comp.type === "editgrid") {
      const layout = buildLayoutFromDatagrid(comp);
      if (layout?.selectKey) found = true;
    }
  });
  return found;
}

export async function prepareFormSchemaWithCaddies(
  schema,
  apiUrl,
  submissionData = null
) {
  if (!schema?.components) {
    return { schema, caddiesById: new Map(), hasCaddieSelect: false };
  }
  const hasCaddieSelect = formSchemaHasCaddieSelect(schema.components);
  if (!hasCaddieSelect) {
    return { schema, caddiesById: new Map(), hasCaddieSelect: false };
  }
  const next = JSON.parse(JSON.stringify(schema));
  ensureCaddieDatagridLayoutFromSchema(next.components);
  const caddies = await fetchCaddieOptions(apiUrl);
  injectCaddieSelectValues(next.components, caddies, submissionData);
  ensureDatagridRowActions(next.components);
  return {
    schema: next,
    caddiesById: buildCaddiesById(caddies),
    hasCaddieSelect: true,
  };
}

export function applyCaddieOnFormChange(submission, formio, caddiesById) {
  if (!submission?.data) return {};
  const data = JSON.parse(JSON.stringify(submission.data));
  if (!submission.changed) return data;

  let layout = getCaddieDatagridLayout();
  if (!layout?.gridKey && formio?.form?.components) {
    layout = ensureCaddieDatagridLayoutFromSchema(formio.form.components);
  }
  const changed = submission.changed;
  const comp = changed?.component;
  let changedKey =
    (comp && isCaddieSelectComponent(comp) ? comp.key : null) ||
    comp?.key ||
    resolveCaddieChangedKey(changed, layout);
  if (!changedKey && !isDefaultRadioFormChange(changed, layout)) return data;

  if (isDefaultRadioFormChange(changed, layout)) {
    // Use submission data as base — mergeCaddieGridFromFormioInstance reads DOM and can
    // pick the wrong row when multiple per-row radios briefly appear checked.
    let next = JSON.parse(JSON.stringify(data));
    const changedRow = resolveDatagridRowIndex(changed, layout, next);
    if (changedRow >= 0 && layout.defaultRadioKey) {
      const grid = Array.isArray(next[layout.gridKey])
        ? next[layout.gridKey].map((r) => ({ ...r }))
        : [];
      if (changedRow < grid.length) {
        const inst = changed.instance;
        let val;
        try {
          val = inst?.dataValue;
        } catch {
          val = undefined;
        }
        if (val === undefined && typeof inst?.getValue === "function") {
          try {
            val = inst.getValue();
          } catch {
            val = undefined;
          }
        }
        if (val === undefined && changed?.value !== undefined) {
          val = changed.value;
        }
        if (val !== undefined) {
          grid[changedRow] = { ...grid[changedRow], [layout.defaultRadioKey]: val };
          next[layout.gridKey] = grid;
        }
      }
    }
    let selectedIdx = changedRow;
    if (selectedIdx < 0 && formio) {
      selectedIdx = getSelectedDefaultRadioRowFromFormio(formio, layout);
    }
    if (selectedIdx < 0) {
      selectedIdx = findLastDefaultRadioRowIndex(next[layout.gridKey], layout);
    }
    if (selectedIdx >= 0) {
      next = applyExclusiveDefaultRadioAtIndex(next, layout, selectedIdx);
    } else {
      next = applyDefaultCaddieRadioExclusive(next, layout, changed);
    }
    next = syncPresentCaddieFromGrid(next, layout, caddiesById, {
      syncFlatFields: false,
    });
    if (formio) {
      syncExclusiveDefaultRadioToFormio(formio, next, layout);
    }
    return next;
  }

  if (layout?.selectKey && isDatagridCaddieChange(changed, layout)) {
    changedKey = layout.selectKey;
  }

  const isGrid = isGridCaddieSelectChange(changedKey, changed, layout, data);
  const isTop = isTopLevelCaddieSelectKey(changedKey, changed, layout);
  if (!isGrid && !isTop) return data;

  return applyCaddieSelectionToFormio(
    formio,
    data,
    caddiesById,
    isGrid ? layout.selectKey : changedKey,
    changed
  );
}

/** Default/active caddie from saved person_data (registration + other pages). */
export function getDefaultCaddieFromPersonData(personData) {
  if (!personData || typeof personData !== "object") return null;

  const layout = getCaddieDatagridLayout();
  const presentList = getPresentCaddiesList(personData, layout);
  if (presentList[0]) return presentList[0];
  const defaultId = resolveDefaultCaddieIdFromGrid(personData, layout);
  if (!defaultId) return null;

  for (const gridKey of CADDIE_STORAGE_GRID_KEYS) {
    const grid = personData[gridKey];
    if (grid?.[KNOWN_CADDIES_KEY] && Array.isArray(grid[KNOWN_CADDIES_KEY])) {
      const match = grid[KNOWN_CADDIES_KEY].find(
        (kc) => extractCaddieIdForStorage(kc?._id ?? kc) === defaultId
      );
      if (match) return match;
    }
  }
  return { _id: defaultId };
}

export function isRegistrationFormId(formId) {
  return String(formId || "").trim() === REGISTRATION_FORM_ID;
}

/**
 * Respect Form Builder datagrid settings (addAnother text, position, remove row).
 * Only unblock row add/remove when the schema explicitly disabled them.
 */
export function ensureDatagridRowActions(components) {
  if (!Array.isArray(components)) return;
  traverseComponents(components, (component) => {
    const type = (component.type || "").toLowerCase();
    if (type !== "datagrid" && type !== "editgrid") return;
    if (component.disableAddingRemovingRows === true) {
      component.disableAddingRemovingRows = false;
    }
  });
}
